import { Fragment, useRef, useState } from 'react';
import { Skeleton } from './ui/Skeleton';
import { Spinner } from './ui/Spinner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getPackageSubcontracts, getExecution, getAcceptance, getFinalInspection, rmb, rmbCents, inclTaxLabel, TAX_PCT_MISSING_ERROR, TAX_PCT_MISSING_HINT,
  apiErrText as errText, fileSizeText, packageFileDownloadUrl,
  CERT_STATE, CERT_KIND_LABEL, ALERT_KIND_LABEL, CRITERION_STATUS, SEVERITY_LABEL, nextCertState, prevCertState, execActions, acceptanceActions, finalInspectionActions,
  getMe, getFinalInspectionLoop, listStaff, finalInspectionLoopActions, loopPhotoUrl, LOOP_STATUS,
  staffWiLedger, staffComplianceFileUrl, staffComplianceConfirm, CERT_FAPIAO_ACCEPT, canUploadCertFapiao, beijingStamp, certStateZh as stateZh,
  type SubcontractRow, type ExecutionSummary, type AcceptanceSummary, type FinalInspectionSummary, type FinalInspectionFinding, type FindingSeverity,
  type ContractValueView, type InstructionLedger,
  type MeResponse, type FinalInspectionLoop, type LoopFinding, type StaffRow,
  type PaymentScheduleRow, type PaymentPlanMilestoneInput, type UnsignSubcontractResult, type AmendContractValueResult,
  type PaymentMode, type SettlementStage, type PaymentTermsExtractResult, type SetPaymentPlanBody,
  type ExecPaymentCert, type UploadCertFapiaoResult,
} from '../lib/api';
import ContractValueBlock from './ContractValueBlock';
import RevertSignature from './RevertSignature';
import AmendContractValue, { AmendmentBreakdown, AwardVsContractLine } from './AmendContractValue';

/**
 * 执行与结算 / Execution & Closeout console (V8/V9/V11). One subcontract's money flow with live actions:
 * the reversible 5-state payment-cert flow, the §14·0 advance recovery, the legacy change-order history,
 * the §16d/§16e settlement with its audit gate, and calendar alerts. Every gate is re-derived server-side.
 *
 * Eric 9/18 (工程指令): PR10b retired the OLD 变更 flow here. `change_orders` is single-line, carries no
 * signatures and no labor-facing state, and `incorporateChangeOrder()` rewrites `subcontracts.value_rmb`
 * — which is exactly the mistake §1.11 exists to prevent, since 4.2.3/4.2.4 pay a percentage of the
 * baseline. Nothing server-side changed (中信's rows must stay readable, and settlement still reads them
 * until Layer 3), so the retirement is a UI one: the section is history, and new work goes through
 * 「工程指令」, where it gets three signatures, a quote clock and a 认价单.
 */
export function ExecutionPanel({ pid, isAwarded, awardedBidTotal, awardedTaxPct = null }: { pid: string; isAwarded: boolean; awardedBidTotal: number | null; awardedTaxPct?: number | null }) {
  const { data: subs, isLoading } = useQuery({ queryKey: ['subcontracts', pid], queryFn: () => getPackageSubcontracts(pid) });
  const [scId, setScId] = useState<string | null>(null);

  if (isLoading) return <div className="card"><Skeleton lines={3} /></div>;
  if (!subs || subs.length === 0) {
    if (!isAwarded) return <div className="empty">尚未定标 / Not awarded yet — <a href={`/packages/${pid}?tab=compare`}>先在「对比与定标」页定标 / award a bidder on the Compare tab</a>，再回来生成分包合同。</div>;
    return <GenerateSubcontractForm pid={pid} awardedBidTotal={awardedBidTotal} awardedTaxPct={awardedTaxPct} />;
  }
  const chosen = scId ?? subs[0].id;
  const chosenName = subs.find((s) => s.id === chosen)?.labor_co_name ?? null;

  return (
    <div>
      {subs.length > 1 && (
        <div className="row-wrap" style={{ marginBottom: 12 }}>
          {subs.map((s) => (
            <button key={s.id} className={s.id === chosen ? 'btn-primary' : ''} onClick={() => setScId(s.id)}
              title={`合同额 / value ${inclTaxLabel(s.value_rmb, s.value_incl_tax_rmb, s.final_tax_pct)}`}>
              {s.labor_co_name ?? s.id} · {s.final_tax_pct != null && s.value_incl_tax_rmb != null
                ? `含税 ${rmb(s.value_incl_tax_rmb)}`
                : rmb(s.value_rmb)}
            </button>
          ))}
        </div>
      )}
      <ExecutionDetail pid={pid} scId={chosen} laborCoName={chosenName} />
    </div>
  );
}

function StatePill({ state }: { state: string }) {
  const m = CERT_STATE[state] ?? { label: state, pill: 'pill-muted' };
  return <span className={`pill ${m.pill}`}>{m.label}</span>;
}

/**
 * 改改 9/22 (fb_49bb4f23 「项目经理那边未看到审核按钮，后续都有哪些审核环节」 · fb_91161ab6 「确认甲方已付款…预算去找财务核实？」).
 * Eric: 预算 keeps pressing every step — the strip only SHOWS the chain, who did each step and when, and for the
 * next one who has to act off-system before 预算 records it.
 */
const CERT_STEPS: { key: string; label: string; hint?: string }[] = [
  { key: 'created', label: '已创建 · 预算申请 / created' },
  { key: 'pm_signed', label: '项目经理签字 / PM sign-off', hint: '项目经理线下签字后，由预算登记 / recorded by 预算 once the PM has signed on paper' },
  { key: 'procurement_approved', label: '采购审核 / procurement check' },
  { key: 'fapiao_attached', label: '发票已附 / invoice attached', hint: '需先上传并确认发票 / upload and confirm the invoice first' },
  { key: 'finance_released', label: '已付款 / paid', hint: '财务付款后由预算登记 / recorded by 预算 after 财务 pays' },
];
const UPSTREAM_STEP = {
  key: 'upstream', label: '甲方已付款 / client paid WTL',
  hint: '请与财务核实甲方款项已到账后，由预算确认 / check with 财务 that the client\'s payment has arrived, then 预算 confirms',
};
/** The pre-9/21 client posted these literals as the actor, so they name nobody. */
const LEGACY_ACTOR = /^\s*(操作员(\s*\/\s*op)?|op)\s*$/i;
const actorName = (a: string | null | undefined) => (!a || LEGACY_ACTOR.test(a) ? '未记录姓名 / name not recorded' : a);
/** 预算 presses every step, so the name on a done step is the one who RECORDED it. */
const recordedBy = (a: string | null | undefined, when: string) => {
  const t = when ? ` · ${when}` : '';
  return !a || LEGACY_ACTOR.test(a) ? `未记录姓名 / name not recorded${t}` : `由 ${a} 登记${t} / recorded by ${a}`;
};
/** What the 发票 step is waiting on, read off the H2 slot (review 9/22). */
function fapiaoHint(status: ExecPaymentCert['fapiao_status']): string {
  switch (status) {
    case 'returned': return '发票已退回，重新上传并确认后方可付款 / the invoice was returned — upload and confirm a new one before payment';
    case 'uploaded': return '发票待确认，确认后方可付款 / the invoice awaits confirmation — confirm it before payment';
    case 'waived': return '发票记录已被免除，请由总经理/预算在「履约档案」取消免除后再上传 / the invoice record was waived — un-waive it in the compliance file, then upload';
    case 'confirmed': return '发票已确认，由预算推进 / the invoice is confirmed — 预算 moves the cert on';
    default: return '需先上传并确认发票，方可付款 / upload and confirm the invoice before payment';
  }
}

function CertApprovalSteps({ cert, backToBack, frozen, fapiaoOk }: { cert: ExecPaymentCert; backToBack: boolean; frozen: boolean; fapiaoOk: boolean }) {
  const [open, setOpen] = useState(false);
  // defensive: a malformed trail entry is skipped, never thrown on
  const history = Array.isArray(cert.state_history)
    ? cert.state_history.filter((e) => !!e && typeof e === 'object' && typeof e.to === 'string')
    : undefined;
  const at = CERT_STEPS.findIndex((s) => s.key === cert.state);
  // review 9/22 · at 发票已附 with a returned / pending / waived / missing invoice the cert cannot be paid, so the
  // 发票 step is still the one to do (the server 409s → 已付款 until the invoice is confirmed).
  const fapiaoBlocked = cert.state === 'fapiao_attached' && !fapiaoOk;
  // who / when = the LAST non-reverted transition INTO that state (a re-done step shows its latest doer)
  const lastInto = (state: string) => {
    for (let i = (history?.length ?? 0) - 1; i >= 0; i--) { const e = history![i]; if (e.to === state && !e.reverted) return e; }
    return null;
  };
  // `by` = the 「由 … 登记」 line; an older server sends no trail (or no upstream_received_by) — then just ✓
  type Step = { key: string; label: string; hint?: string; done: boolean; by: string | null };
  const steps: Step[] = CERT_STEPS.map((s, i) => {
    const done = i <= at && !(s.key === 'fapiao_attached' && fapiaoBlocked);
    const e = done ? lastInto(s.key) : null;
    const hint = s.key === 'fapiao_attached' ? fapiaoHint(cert.fapiao_status) : s.hint;
    return { ...s, hint, done, by: e ? recordedBy(e.actor, beijingStamp(e.at)) : done && history ? recordedBy(null, '') : null };
  });
  if (backToBack) {
    const done = !!cert.upstream_received_at;
    const when = beijingStamp(cert.upstream_received_at);
    steps.splice(4, 0, { ...UPSTREAM_STEP, done,
      by: !done ? null : cert.upstream_received_by !== undefined ? recordedBy(cert.upstream_received_by, when) : when || null });
  }
  const released = cert.state === 'finance_released';
  const nextIdx = released || frozen ? -1 : steps.findIndex((s) => !s.done);
  const next = nextIdx >= 0 ? steps[nextIdx] : null;
  const reverts = (history ?? []).filter((e) => e.reverted).length;
  return (
    <div className="small">
      <div className="cert-steps-strip">
        <span className="muted" style={{ fontWeight: 600 }}>审批进度 / approval steps</span>
        {steps.map((s, i) => (
          <span key={s.key} className="cert-step">
            {i > 0 && <span className="muted" aria-hidden="true">→</span>}
            {s.done
              ? <><span className="pill pill-good">✓ {s.label}</span>{s.by && <span className="muted">{s.by}</span>}</>
              : i === nextIdx
                ? <span className="pill pill-warn">下一步 / next · {s.label}</span>
                : <span className="muted">{s.label}{released ? '（未登记 / not recorded）' : ''}</span>}
          </span>
        ))}
        {reverts > 0 && history && (
          <button type="button" className="small" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            历史 ({history.length}) / history {open ? '▴' : '▾'}
          </button>
        )}
      </div>
      {next?.hint && <div style={{ color: 'var(--accent)', marginTop: 3 }}>下一步 / next：{next.hint}</div>}
      {frozen && !released && <div className="muted" style={{ marginTop: 3 }}>已进入结算，此凭证不再推进 / a settlement exists — this cert moves no further</div>}
      {open && history && (
        <ol style={{ margin: '4px 0 0', paddingLeft: 20 }}>
          {history.map((e, i) => (
            <li key={i}>
              {beijingStamp(e.at)} · {actorName(e.actor)} · {stateZh(e.from)} → {stateZh(e.to)}
              {e.reverted
                ? <span style={{ color: 'var(--warn)' }}> · 退回 / reverted{e.notes ? `：${e.notes}` : ''}</span>
                : e.notes ? <span className="muted"> · {e.notes}</span> : null}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8 }}><strong>{title}</strong><span className="spacer" />{action}</div>
      {children}
    </div>
  );
}

/** 改改 9/21 (fb_8ef675e1) · a button that opens the file dialog for a payment cert's 发票 (up to 10 files;
 *  PDF / OFD / images — CERT_FAPIAO_ACCEPT, the route's own list). The hidden input is
 *  cleared after every pick, so choosing the same file again — e.g. after a refusal — still fires. */
function FapiaoPicker({ label, title, className, disabled, onFiles }: {
  label: string; title?: string; className?: string; disabled?: boolean; onFiles: (files: File[]) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <button type="button" className={className} disabled={disabled} title={title} onClick={() => ref.current?.click()}>{label}</button>
      <input ref={ref} type="file" multiple accept={CERT_FAPIAO_ACCEPT} style={{ display: 'none' }} disabled={disabled}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          e.target.value = '';
          if (files.length > 0) onFiles(files);
        }} />
    </>
  );
}

function ExecutionDetail({ pid, scId, laborCoName }: { pid: string; scId: string; laborCoName: string | null }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [certMs, setCertMs] = useState('');
  const [certAmt, setCertAmt] = useState('');
  const [retDlp, setRetDlp] = useState('');
  const [gmOk, setGmOk] = useState(false);
  const [dlpOk, setDlpOk] = useState(false);
  const { data, isLoading, isError, error } = useQuery<ExecutionSummary>({ queryKey: ['execution', scId], queryFn: () => getExecution(scId) });
  // Eric 9/18 (工程指令): §1.11's figures come off the 工程指令 ledger — there is no lighter route, and the
  // 工程指令 tab already caches this exact key, so switching tabs costs nothing. A 项目经理 must never fire
  // it (403 forbidden_site_pm, and the whole point is they never see a price); everyone else on this
  // panel is a money role already, since `/subcontracts/:scId/execution` is money-guarded too.
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const isPm = me?.account?.role === 'site_pm';
  // 改改 9/21 (fb_8ef675e1) · the 发票 upload route admits gm / budget / procurement / commercial / finance only
  const canFapiao = canUploadCertFapiao(me?.account?.role);
  // …and confirming it is the 履约档案 review action (compliance.ts `review`, CompliancePanel REVIEW_ROLES)
  const canConfirmFapiao = ['gm', 'budget', 'procurement', 'commercial'].includes(me?.account?.role ?? '');
  const { data: ledger } = useQuery<InstructionLedger>({
    queryKey: ['wi-ledger', scId],
    queryFn: () => staffWiLedger(scId),
    enabled: !!me && !isPm,
    // An outage of the advisory block must never look like an outage of the payment console.
    retry: false,
  });
  const cv: ContractValueView | null = ledger?.contract_value ?? null;

  // okText may be a function of the action's result (the 发票 upload says whether the cert moved).
  async function act<T>(fn: () => Promise<T>, okText: string | ((r: T) => string) = '已更新 / done') {
    setBusy(true); setMsg(null);
    try {
      const r = await fn();
      await qc.invalidateQueries({ queryKey: ['execution', scId] });
      await qc.invalidateQueries({ queryKey: ['subcontracts', pid] });
      setMsg({ kind: 'ok', text: typeof okText === 'function' ? okText(r) : okText });
    } catch (e: any) {
      const body = e?.body;
      const text = body?.requiresAudit ? '需先审计后才能批准 / audit required before approval'
        : body?.reason === 'standards_not_confirmed' ? '劳务方尚未确认验收标准 — 确认后方可签署 / labor must confirm the 验收标准 first'
        : body?.reason === 'contract_not_generated' ? '请先在「验收标准与合同」页生成合同 Word 并完成盖章签字，再记录签署 / generate + execute the contract on the Standards tab first'
        // 华控 9/16 · the signature must carry the executed paper
        : body?.reason === 'executed_scan_required' ? '请在「验收标准与合同」页上传已盖章签字的合同扫描件并填写签署日期 / attach the executed contract scan + signing date on the Standards tab'
        : body?.reason === 'signed_on_invalid' ? '签署日期无效 — 需为真实日期，不能是将来的日期，也不能早于 2020 年 / signed_on must be a real date, not in the future and not before 2020'
        : body?.reason === 'unsupported_scan_type' ? '扫描件只接受 PDF 或图片（JPG/PNG/HEIC/WEBP）/ the scan must be a PDF or an image (JPG/PNG/HEIC/WEBP)'
        // 改改 9/20 · payment now runs on 含税, so a lock with no 税率 can no longer be paid against.
        : body?.error === 'tax_pct_missing' ? TAX_PCT_MISSING_ERROR
        // 改改 9/21 · the newer routes send { error, reason, detail } — a bilingual detail beats the bare reason
        : body?.reason && !(typeof body?.detail === 'string' && body.detail.trim()) ? `操作被拒 / rejected: ${body.reason}`
        // bilingual server detail (e.g. no_advance_terms / reason_required / the 撤销签署 409s), then the
        // ERR_COPY fallback for a bare code, then the transport error — apiErrText already ranks these.
        : errText(e);
      setMsg({ kind: 'err', text });
    } finally { setBusy(false); }
  }

  // 改改 9/20 (fb_3267bf3b) · after 撤销签署 the contract is a draft again, the advance ledger + advance
  // cert are gone and the package is back at st.12 — refresh everything that reads any of the three.
  async function revertDone(result: UnsignSubcontractResult) {
    // rig 9/20: the package was already at st.12, so the server answered {from: 12, to: 12} and the toast
    // still announced a rollback. Report the stage only when one actually moved.
    const { from, to } = result.package_stage;
    const moved = from != null && to != null && from !== to;
    setMsg({ kind: 'ok', text: moved
      ? `签署已撤销 · 合同回到草稿，采购包回到第 ${to} 阶段 / signature reverted — back to draft (st.${to})`
      : '签署已撤销 · 合同回到草稿 / signature reverted — back to draft' });
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['execution', scId] }),
      qc.invalidateQueries({ queryKey: ['subcontracts', pid] }),
      qc.invalidateQueries({ queryKey: ['package', pid] }),
    ]);
  }

  // 改改 9/21 · everything that reads the contract value or its 税率: the execution summary (plan, advance,
  // certs), the package's subcontract list, the package, and ['wi-ledger', scId] — what feeds
  // ContractValueBlock above (and the 工程指令 tab's copy of the same block).
  function refreshValueReads() {
    return Promise.all([
      qc.invalidateQueries({ queryKey: ['execution', scId] }),
      qc.invalidateQueries({ queryKey: ['subcontracts', pid] }),
      qc.invalidateQueries({ queryKey: ['package', pid] }),
      qc.invalidateQueries({ queryKey: ['wi-ledger', scId] }),
      // 改改 9/21 (fb_8ef675e1) · an amendment that moves the advance returns its H2 invoice in the 履约档案
      qc.invalidateQueries({ queryKey: ['compliance'] }),
    ]);
  }

  // 改改 9/21 · 调整合同额 moves value_rmb + the §1.11 baseline, the advance ledger, an un-released 预付款凭证
  // and every 含税 planned amount.
  async function amendDone(result: AmendContractValueResult) {
    const signedNow = (result.subcontract?.status ?? data?.subcontract.status) === 'signed';
    const to = result.to;
    const cert = result.advance_cert;
    const figure = to.value_incl_tax_rmb != null ? `含税 ${rmbCents(to.value_incl_tax_rmb)}` : `不含税 ${rmbCents(to.value_rmb)}`;
    setMsg({ kind: 'ok', text: `${signedNow ? '合同额已按签订版调整' : '合同额已调整'} · ${figure} / contract value amended`
      + (cert && cert.from_state !== cert.to_state ? ' · 预付款凭证需重新推进 / the advance cert must be pushed through again' : '')
      // 改改 9/21 (fb_8ef675e1) · an invoice attached for the old figure no longer matches — the server returned it.
      + (cert?.fapiao_returned ?' · 原发票金额不符已退回，请重新上传发票 / the attached invoice no longer matches — upload a new one' : '') });
    await refreshValueReads();
  }

  // 改改 9/21 · 补录税率 — every 含税 figure on this page (value, plan, advance, certs) is derived from the rate.
  async function taxRateDone(taxPct: number) {
    setMsg({ kind: 'ok', text: `税率已补录为 ${taxPct}% · 含税金额已按此重算 / tax rate set to ${taxPct}% — tax-inclusive figures recomputed` });
    await refreshValueReads();
  }

  // 改改 9/21 (fb_8ef675e1) · 「预付款的凭证应该是发票，这里上传不了」— the cert's invoice is uploaded here and filed in
  // the 履约档案 as H2, so the 履约档案 reads refresh too. `advance` = 采购已核 → 发票已附 in the same step.
  function uploadFapiao(c: ExecPaymentCert, files: File[], opts: { advance?: boolean; replace?: boolean }) {
    return act(async () => {
      const r = await execActions.uploadCertFapiao(c.id, files, opts);
      void qc.invalidateQueries({ queryKey: ['compliance'] });
      return r;
    }, (r: UploadCertFapiaoResult) => r.transitioned
      ? `发票已上传 · 凭证推进 → ${CERT_STATE[r.transitioned.to]?.label ?? r.transitioned.to} / invoice uploaded — cert moved on`
      : opts.replace
        ? `发票已替换（${files.length} 个文件）/ invoice replaced (${files.length} file${files.length > 1 ? 's' : ''})`
        : `发票已附（${files.length} 个文件）· 已存入履约档案 H2 / invoice attached (${files.length} file${files.length > 1 ? 's' : ''}) — filed in the compliance file (H2)`);
  }

  if (isLoading) return <div className="card"><Skeleton lines={3} /></div>;
  if (isError) return <div className="empty" style={{ color: 'var(--bad)' }}>{String((error as any)?.message)}</div>;
  if (!data) return <div className="empty">未找到 / Not found</div>;

  const sc = data.subcontract as SubcontractRow & Record<string, unknown>;
  const ar = data.advance_release;
  const outstanding = ar && ar.advance_amount_rmb != null ? Math.max(0, ar.advance_amount_rmb - (ar.recovered_to_date_rmb ?? 0)) : null;
  const milestones: Array<{ id: string; label_zh?: string; label_en?: string; pct?: number }> = (() => {
    try { return JSON.parse(String(sc.milestones_json ?? '[]')); } catch { return []; }
  })();
  // one progress cert per milestone (server enforces) — only offer milestones not yet certified
  const uncertified = milestones.filter((m) => !data.payment_certs.some((c) => c.cert_kind === 'progress' && c.milestone_id === m.id));
  // 改改 9/21 (fb_9cf4aaea) · 按节点支付: the 首期款 is not recovered, each stage pays its % of the contract as is.
  const installment = planModeOf(data) === 'installment';
  // 改改 9/21 · the dropdown speaks her unit — 本期实付占合同额 % — not the gross 计量 % the plan stores.
  // The server's payable_pct first; the same conversion the batch editor uses when an older server omits it
  // (none in installment mode: there the stored pct already IS the stage's share of the contract).
  const cashPctOf = (m: { id: string; pct?: number }): number | null => {
    const row = data.payment_schedule?.rows.find((r) => r.kind === 'progress' && r.milestone_id === m.id);
    if (row?.payable_pct != null) return row.payable_pct;
    if (m.pct == null) return null;
    return installment ? m.pct : round2(m.pct * cashFactor(sc.advance_pct, sc.retention_pct) / 100);
  };
  const advanceWord = installment ? '首期款' : '预付款';
  // review R8 (改改 9/21): once 结算 has started the settlement balance pays what is left — the server refuses a
  // new 预付款/进度款 cert, moving one forward, and stepping a released one back (409 settlement_exists).
  const settledStarted = data.settlements.length > 0;
  const frozenAfterSettlement = (c: { cert_kind: string }) => settledStarted && (c.cert_kind === 'advance' || c.cert_kind === 'progress');
  // 改改 9/21 · under 按节点支付 the 首期款 + the stages may not pass the settle-to line (the server 400s
  // installments_exceed_settlement on advance-terms too) — the editor says so before it is asked.
  const advanceCeiling = installment && sc.settlement_stage
    ? round2(sc.settlement_stage.settle_to_pct - milestones.reduce((t, m) => t + (m.pct ?? 0), 0))
    : null;
  // 改改 9/21 · the contract value moves until money has been drawn against the plan: the server refuses once
  // a 进度款凭证 or a 结算 exists, so the control is not offered there.
  const valueAmendable = data.settlements.length === 0 && !data.payment_certs.some((c) => c.cert_kind === 'progress');

  return (
    <div>
      {msg && <div className="card" style={{ borderColor: msg.kind === 'err' ? 'var(--bad)' : 'var(--good)', color: msg.kind === 'err' ? 'var(--bad)' : 'var(--good)' }}>{msg.text}</div>}

      {/* §1.11 — 基准 vs 核定, above everything that spends money on this contract. */}
      {cv && <ContractValueBlock cv={cv} title="当前核定合同价（含税）/ Current approved contract value (tax-inclusive)" />}

      <Section title={`分包合同 / Subcontract${sc.contract_id ? ` · ${sc.contract_id}` : ''}`}>
        <div className="row-wrap">
          <span className="pill pill-muted">{laborCoName ?? sc.labor_co_name ?? '—'}</span>
          <span className={`pill ${sc.status === 'signed' ? 'pill-good' : 'pill-stage'}`}>{sc.status}</span>
          <span className="muted small" style={{ wordBreak: 'break-word' }}>合同额 / value {inclTaxLabel(sc.value_rmb, sc.value_incl_tax_rmb, sc.final_tax_pct)}</span>
          {installment
            ? <span className="muted small" title="按节点支付：首期款签订后支付，不从后续各期扣回 / installments: the first payment is not recovered from the later stages">
                首期款 / first payment {sc.advance_pct != null ? `${sc.advance_pct}%（不抵扣）` : '无 / none'}
                {sc.settlement_stage ? ` · 结算付至 ${fmtPct(sc.settlement_stage.settle_to_pct)}% / settle to ${fmtPct(sc.settlement_stage.settle_to_pct)}%` : ''}
              </span>
            : <span className="muted small">预付 / advance {sc.advance_pct != null ? `${sc.advance_pct}%` : '无 / none'}</span>}
          <span className="muted small">质保 / retention {sc.retention_pct ?? '—'}%</span>
          <span className="muted small">DLP {sc.dlp_months ?? '—'}m</span>
          {sc.status !== 'signed' && (
            <>
              <span className={`pill ${sc.standards_confirmed_at ? 'pill-good' : 'pill-warn'}`} title="WS4 · 签署前劳务方须确认验收标准">
                验收标准 {sc.standards_confirmed_at ? '已确认 ✓' : '待劳务确认'}
              </span>
              {/* 华控 9/16: this used to be a bare 「签署合同 / Sign」 that POSTed on one click — a second way
                  to mark a contract signed with no paper behind it. 记录已签署 now needs the chopped scan +
                  签署日期, so it lives in ONE place, next to the document it refers to. */}
              <a className="small" href={`/packages/${pid}?tab=standards`}
                title="记录签署需上传签章扫描件与签署日期，在「验收标准与合同」页办理 / recording a signature needs the executed scan + date, on the Standards tab">
                ✍ 在「验收标准与合同」页记录已签署 / record the signature on the Standards tab →
              </a>
            </>
          )}
          {/* 改改 9/20 (fb_3267bf3b): 「已签署」 must show the paper it stands on, wherever it is claimed —
              and a signature recorded against an unchopped contract must be undoable here too. */}
          {sc.status === 'signed' && (
            <>
              {sc.signed_on && <span className="muted small">签署日 / signed on {sc.signed_on}</span>}
              {data.executed_file ?? sc.executed_file
                ? (() => {
                    const f = (data.executed_file ?? sc.executed_file)!;
                    return <a className="small" href={packageFileDownloadUrl(pid, f.id)}
                      title={f.file_name}>⬇ 签章扫描件 / executed scan{f.size_bytes ? `（${fileSizeText(f.size_bytes)}）` : ''}</a>;
                  })()
                : <span className="muted small">（无扫描件存档 / no executed scan on file）</span>}
              <RevertSignature scId={scId} role={me?.account?.role} disabled={busy} onDone={revertDone} />
            </>
          )}
        </div>
        {/* 改改 9/21 · 「合同额在中标价基础上有调整」 — how the contract value came to differ from the 议价 lock:
            the recorded amendment (中标价 ± 调整项 = 合同额), or else the two figures side by side. */}
        {sc.value_amendment ? <AmendmentBreakdown sc={sc} /> : <AwardVsContractLine sc={sc} />}
        {/* 改改 9/21 · a SIGNED contract whose lock never carried a 税率 can amend nothing (tax_pct_missing) and can
            no longer 撤销锁定 — so the rate comes first, here, in place of the amend control. */}
        {sc.status === 'signed' && sc.final_tax_pct == null
          ? <TaxRateEditor scId={scId} role={me?.account?.role} disabled={busy} onDone={taxRateDone} />
          : valueAmendable && <AmendContractValue sc={sc} role={me?.account?.role} disabled={busy} onDone={amendDone} />}
        {/* 改改 9/20 (fb_f81de749): 「按默认条款建立」 makes the subcontract with NO advance, and nothing let
            anyone add one afterwards — so 预付款 could not be requested on a contract whose paper says 10%.
            Editable until the first cert exists; once signed the change needs a reason (the paper governs).
            (改改 9/22 · Eric 9/23: new subcontracts follow the template's 4.2.2 「本合同无预付款」, so this is where a
            contract that DOES advance says so.) */}
        {data.payment_certs.length === 0 && (
          <AdvanceTermsEditor scId={scId} signed={sc.status === 'signed'} currentPct={sc.advance_pct ?? null} installment={installment}
            ceiling={advanceCeiling} busy={busy} act={act} />
        )}
      </Section>

      {/* 付款计划 / payment plan (改改 9/20 · fb_3267bf3b) — the per-batch %/金额 she asked for, above the
          certs that are raised against it. */}
      {/* keyed: an open editor / an AI reading belongs to ONE subcontract — switching contracts drops it. */}
      <PaymentPlanSection key={scId} scId={scId} data={data} busy={busy} act={act} role={me?.account?.role} />

      {/* payment certs — reversible 5-state flow */}
      <Section title={`付款凭证 / Payment certs (${data.payment_certs.length})`}>
        {/* 4.2.3/4.2.4 read a percentage of the BASELINE. The certified amount is typed right below, so
            the two numbers and the caveat that separates them belong here too (read-only, advisory). */}
        {cv && <ContractValueBlock cv={cv} compact title="计量参考（含税）/ Reference for this certificate (tax-inclusive)" />}
        {data.payment_certs.length === 0 ? <div className="muted small">—</div> : (
          <div className="cmp-wrap"><table className="cmp cmp-fit"><thead><tr>
            <th>类型 / kind</th><th>里程碑</th><th>金额（含税）/ certified (tax-incl.)</th><th>状态 / state</th><th>发票 / invoice</th><th className="col-actions">操作 / actions</th>
          </tr></thead><tbody>
            {data.payment_certs.map((c) => {
              const frozen = frozenAfterSettlement(c);
              const next = frozen ? null : nextCertState(c.state);
              const prev = frozen && c.state === 'finance_released' ? null : prevCertState(c.state);
              // 改改 9/21 (fb_8ef675e1) · → 发票已附 needs the invoice on file (the server 409s fapiao_required), so at
              // 采购已核 without one the step forward IS the upload. A settlement-frozen cert has no `next`, so it is
              // never offered the advancing variant — only the plain attach / replace in the 发票 column.
              const fapiaoFiles = c.fapiao_files ?? [];
              const hasFapiao = fapiaoFiles.length > 0;
              // review 9/21 · only a CONFIRMED invoice opens → 发票已附 and → 已付款 (a returned or still-pending one does not)
              const fapiaoOk = c.fapiao_status === 'confirmed' && hasFapiao;
              const confirmed = c.fapiao_status === 'confirmed';
              const returned = c.fapiao_status === 'returned';
              // a contractor (re-)upload awaiting review — confirm it here rather than upload over it
              const pending = c.fapiao_status === 'uploaded';
              // a waived H2 slot refuses every upload (409) until 总经理/预算 un-waive it in the 履约档案
              const waived = c.fapiao_status === 'waived';
              const pickers = canFapiao && !waived;
              const released = c.state === 'finance_released';
              const needsFapiao = (next === 'fapiao_attached' || next === 'finance_released') && !fapiaoOk;
              const uploadAndAdvance = pickers && needsFapiao && next === 'fapiao_attached' && !pending;
              const confirmable = pending && canConfirmFapiao && !!c.fapiao_item_id;
              const b2bPending = next === 'finance_released' && data.subcontract.payment_linkage === 'back_to_back' && !c.upstream_received_at;
              // at 发票已附 without a valid invoice the attach / replace picker is THE next step, so it is the prominent
              // one — except over a pending upload, where 替换 would discard the contractor's files.
              const pickerClass = needsFapiao && !uploadAndAdvance && !pending ? 'btn-primary small' : 'small';
              return (
                <Fragment key={c.id}>
                <tr className="cert-main">
                  <td>{installment && c.cert_kind === 'advance' ? '首期款' : CERT_KIND_LABEL[c.cert_kind] ?? c.cert_kind}</td>
                  <td className="muted">{c.milestone_id ?? '—'}</td>
                  <td>{rmbCents(c.certified_amount_rmb)}</td>
                  <td><StatePill state={c.state} /></td>
                  {/* 改改 9/22 · a long 发票 name is one ellipsed line (full name on hover), so 操作 stays on the card */}
                  <td className="cell-wrap">
                    {hasFapiao
                      ? fapiaoFiles.map((f) => (
                          <div key={f.id} className="small" style={{ wordBreak: 'break-word' }}>
                            <a className="file-chip" href={staffComplianceFileUrl(f.id)} title={`下载发票 / download invoice · ${f.file_name}`}>📎 {f.file_name}</a>
                            {f.size_bytes ? <span className="muted">（{fileSizeText(f.size_bytes)}）</span> : null}
                          </div>
                        ))
                      : <span className="muted small">未附发票 / no invoice</span>}
                    {c.fapiao_note && <div className="muted small" style={{ wordBreak: 'break-word' }}>{c.fapiao_note}</div>}
                    {returned && (
                      <div className="small" style={{ color: 'var(--warn)', wordBreak: 'break-word', marginTop: 2 }}>
                        <span className="pill pill-warn small">已退回 / returned</span>
                        {c.fapiao_review_note ? ` ${c.fapiao_review_note}` : ''}
                      </div>
                    )}
                    {c.fapiao_status === 'uploaded' && (
                      <div style={{ marginTop: 2 }}>
                        <span className="pill pill-muted small" title="发票已上传，待在履约档案 H2 确认 / uploaded — awaiting confirmation in the compliance file (H2)">待确认 / pending</span>
                      </div>
                    )}
                    {waived && (
                      <div style={{ marginTop: 2 }}>
                        <span className="pill pill-muted small">已免除 / waived</span>
                        <div className="muted small" style={{ wordBreak: 'break-word' }}>
                          发票记录已被免除，请由总经理/预算在「履约档案」取消免除后再上传 / the invoice record was waived — a GM or budget user must un-waive it in the compliance file first
                        </div>
                      </div>
                    )}
                    {/* attach from 已创建 on (her 预付款 凭证 IS the invoice); replace unless a paid cert's invoice is
                        confirmed; add only onto a confirmed invoice (an add to an unconfirmed slot does not confirm it). */}
                    {pickers && (
                      <div className="row" style={{ gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                        {!hasFapiao && (
                          <FapiaoPicker className={pickerClass} disabled={busy}
                            label={released ? '📎 补附发票 / attach invoice' : '📎 附发票 / attach invoice'}
                            title="附上发票（不改变凭证状态），同时存入履约档案 H2 / attach the invoice without moving the cert; also filed in the compliance file (H2)"
                            onFiles={(fs) => uploadFapiao(c, fs, {})} />
                        )}
                        {hasFapiao && !(released && confirmed) && (
                          <FapiaoPicker className={pickerClass} disabled={busy} label="替换 / replace"
                            title="用新选的文件替换已附发票 / replace the attached invoice with the files you pick"
                            onFiles={(fs) => uploadFapiao(c, fs, { replace: true })} />
                        )}
                        {hasFapiao && confirmed && (
                          <FapiaoPicker className="small" disabled={busy} label="＋补充 / add"
                            title="在已附发票之外补充文件 / add files next to the attached invoice"
                            onFiles={(fs) => uploadFapiao(c, fs, {})} />
                        )}
                      </div>
                    )}
                  </td>
                  <td className="col-actions">
                    <div className="row" style={{ gap: 5 }}>
                      {/* 背靠背: marking the 甲方 receipt is independent of the invoice, so it stays offered */}
                      {b2bPending
                        ? <button className="btn-good" disabled={busy} title="背靠背 / pay-when-paid：先确认甲方已向公司付款，方可放款" onClick={() => act(() => execActions.markUpstreamReceived(c.id), '已确认甲方付款（背靠背）/ client paid — release unlocked')}>确认甲方已付款 ✓</button>
                        : uploadAndAdvance
                          ? <FapiaoPicker className="btn-primary" disabled={busy}
                              label={returned ? '📎 重新上传发票并推进 → / Re-upload invoice & advance' : '📎 上传发票并推进 → / Upload invoice & advance'}
                              title="上传发票后，凭证直接推进到「发票已附」/ upload the invoice and move the cert to 发票已附 in one step"
                              onFiles={(fs) => uploadFapiao(c, fs, { advance: true, replace: hasFapiao })} />
                          : next && !needsFapiao && <button className="btn-good" disabled={busy} onClick={() => act(() => execActions.transitionCert(c.id, next), `凭证推进 → ${CERT_STATE[next]?.label ?? next}`)}>推进 →</button>}
                      {confirmable && (
                        <button className="btn-good" disabled={busy} title="确认劳务方上传的发票（同履约档案 H2 的「确认」）/ confirm the invoice the contractor uploaded (same as 确认 on H2 in the compliance file)"
                          onClick={() => act(async () => {
                            await staffComplianceConfirm(c.fapiao_item_id!);
                            void qc.invalidateQueries({ queryKey: ['compliance'] });
                          }, '发票已确认 / invoice confirmed')}>✓ 确认发票 / confirm invoice</button>
                      )}
                      {needsFapiao && !uploadAndAdvance && !confirmable && !waived && (
                        <span className="muted small">{pending ? '发票待确认 / invoice awaiting confirmation' : '先上传有效发票 / upload a valid invoice first'}</span>
                      )}
                      {c.upstream_received_at && <span className="pill pill-good small" title="甲方已向公司付款（背靠背已满足）">甲方已付款 ✓</span>}
                      {prev && <button disabled={busy} onClick={() => { const n = window.prompt('退回原因 / revert note:'); if (n && n.trim()) act(() => execActions.revertCert(c.id, n.trim()), '凭证已退回 / reverted'); }}>← 退回</button>}
                    </div>
                  </td>
                </tr>
                {/* 改改 9/22 · the approval chain under the cert, full width, so the table gets no wider */}
                <tr className="cert-steps">
                  <td colSpan={6} className="cell-wrap">
                    <CertApprovalSteps cert={c} backToBack={data.subcontract.payment_linkage === 'back_to_back'} frozen={frozen} fapiaoOk={fapiaoOk} />
                  </td>
                </tr>
                </Fragment>
              );
            })}
          </tbody></table></div>
        )}
        {data.payment_certs.length > 0 && (
          <div className="muted small" style={{ marginTop: 6 }}>
            推进到「发票已附」前须先上传发票（PDF / OFD / 图片），发票同时存入履约档案 H2。/ A cert moves to 发票已附 only once the invoice is uploaded (PDF / OFD / image); the invoice is also filed in the compliance file (H2).
          </div>
        )}
        {sc.status === 'signed' && settledStarted && (
          <div className="muted small" style={{ marginTop: 8 }}>已进入结算，剩余款项由结算款支付 / a settlement exists — what is left is paid by the settlement balance</div>
        )}
        {sc.status === 'signed' && !settledStarted && uncertified.length > 0 && (
          <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={certMs} onChange={(e) => setCertMs(e.target.value)} disabled={busy}>
              <option value="">里程碑 / milestone…</option>
              {uncertified.map((m) => {
                const cash = cashPctOf(m);
                const share = cash == null ? '' : installment ? `（占合同额 ${fmtPct(cash)}%）` : `（实付 ${fmtPct(cash)}%）`;
                return <option key={m.id} value={m.id}>{m.label_zh ?? m.label_en ?? m.id}{share}</option>;
              })}
            </select>
            <input type="number" min={0} placeholder="计量金额（含税）¥ / certified (tax-incl.)" title="计量金额按含税填报 / enter the tax-inclusive certified amount" value={certAmt} onChange={(e) => setCertAmt(e.target.value)} style={{ width: 190 }} disabled={busy} />
            <button
              className="btn-primary"
              disabled={busy || !certMs || !(Number(certAmt) > 0)}
              title={installment
                ? '按节点创建进度款凭证（按节点支付：本期按合同额比例支付）/ a progress cert for this stage — installments pay the stage\'s share of the contract'
                : '按里程碑创建进度款凭证（质保金按合同比例自动预扣）'}
              onClick={() => act(async () => {
                await execActions.createProgressCert(scId, { milestone_id: certMs, certified_amount_rmb: Number(certAmt) });
                setCertMs(''); setCertAmt('');
              }, '进度款凭证已创建 / progress cert created')}
            >＋ 创建进度款凭证 / New progress cert</button>
          </div>
        )}
        {/* 预付款凭证 — against the advance ledger, one per subcontract (改改 9/20) */}
        {sc.status === 'signed' && !settledStarted && (
          ar && ar.advance_amount_rmb != null && ar.advance_amount_rmb > 0
            ? (!data.payment_certs.some((c) => c.cert_kind === 'advance') && (
                <div className="row" style={{ gap: 6, marginTop: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    className="btn-primary"
                    disabled={busy}
                    title={`按合同约定的${advanceWord}比例创建${advanceWord}凭证，走同一审批流 / creates the cert for the contractual ${installment ? 'first payment' : 'advance'}; same approval flow`}
                    onClick={() => act(() => execActions.createAdvanceCert(scId), `${advanceWord}凭证已创建 / ${installment ? 'first-payment' : 'advance'} cert created`)}
                  >{installment
                      ? <>＋ 申请首期款（含税）/ Request first payment (tax-incl.) {rmbCents(ar.advance_amount_rmb)}</>
                      : <>＋ 申请预付款（含税）/ Request advance (tax-incl.) {rmbCents(ar.advance_amount_rmb)}</>}</button>
                </div>
              ))
            : (
              <div className="muted small" style={{ marginTop: 8 }}>
                本合同未约定预付款，无法申请。如纸质合同有预付款，请先在上方「分包合同」处设置预付款比例。
                {' '}/ No advance on this subcontract. If the signed paper has one, set the advance % in the 分包合同 block above first.
              </div>
            )
        )}
      </Section>

      {/* 改改 9/21 (fb_9cf4aaea) · under 按节点支付 the 首期款 is never recovered, so 已抵扣/未抵扣 would only mislead. */}
      {ar && (installment
        ? (
          <Section title="首期款 / First payment（不抵扣 / not recovered）">
            <div className="row-wrap">
              <span className="muted small">首期款（含税，不抵扣）/ first payment (tax-incl., not recovered) {rmbCents(ar.advance_amount_rmb)}</span>
            </div>
          </Section>
        )
        : (
          <Section title="预付款释放 / Advance release (§14·0)">
            <div className="row-wrap">
              <span className="muted small">预付（含税）/ advance (tax-incl.) {rmbCents(ar.advance_amount_rmb)}</span>
              <span className="muted small">已抵扣（含税）/ recovered {rmbCents(ar.recovered_to_date_rmb)}</span>
              <span className={`pill ${outstanding === 0 ? 'pill-good' : 'pill-warn'}`}>未抵扣（含税）/ outstanding {rmbCents(outstanding)}</span>
              <span className="muted small">{ar.recovery_basis ?? ''}</span>
            </div>
          </Section>
        ))}

      {/* legacy change orders — history only (PR10 retirement). No create, no price, no incorporate. */}
      <Section title={`旧版变更（历史，只读）/ Legacy change orders (history, read-only) (${data.change_orders.length})`}>
        <div className="small" style={{ color: 'var(--accent)', marginBottom: 8 }}>
          新的工程指令在「工程指令」页签办理 / new instructions live on the 工程指令 tab —{' '}
          <a href={`/packages/${pid}?tab=instructions`}>打开「工程指令」/ Open 工程指令 →</a>
        </div>
        {data.change_orders.length === 0 ? <div className="muted small">—</div> : (
          <div className="cmp-wrap"><table className="cmp"><thead><tr>
            <th>序号</th><th>状态 / status</th><th>定价 / priced</th><th>累计变更 %</th>
          </tr></thead><tbody>
            {data.change_orders.map((c) => (
              <tr key={c.id}>
                <td>#{c.seq}</td>
                <td><span className={`pill ${c.status === 'incorporated' ? 'pill-good' : c.status === 'rejected' ? 'pill-bad' : 'pill-stage'}`}>{c.status}</span></td>
                <td>{rmb(c.priced_amount_rmb)}</td>
                <td className={c.cumulative_variation_pct != null && c.cumulative_variation_pct >= 10 ? 'out-high' : ''}>{c.cumulative_variation_pct != null ? `${c.cumulative_variation_pct}%` : '—'}</td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </Section>

      {/* settlement — net_due / balance / audit gate */}
      <Section title={`结算 / Settlement (${data.settlements.length})`} action={data.settlements.length === 0 ? <button className="btn-primary" disabled={busy} onClick={() => act(() => execActions.draftSettlement(scId), '结算已起草 / drafted')}>起草结算 / Draft</button> : undefined}>
        {/* 改改 9/20 · the 工程指令 ledger is folded into the settlement server-side now, so the old
            「两处来源 — 请勿重复计入」 warning is gone. What is left to say is the formula itself. */}
        <div className="muted small" style={{ marginBottom: 8 }}>
          应付净额 = 当前核定合同价（含税）− 索赔 / net due = current approved value (tax-incl.) − claims
        </div>
        {data.settlements.length === 0 ? <div className="muted small">尚未起草 / not drafted</div> : (
          <div className="cmp-wrap"><table className="cmp"><thead><tr>
            <th>应结（含税）/ net due (tax-incl.)</th><th>结算款（含税）/ balance (§16e, tax-incl.)</th><th>审计 / audit</th><th>状态 / status</th><th>操作 / actions</th>
          </tr></thead><tbody>
            {data.settlements.map((s) => (
              <tr key={s.id}>
                <td>{rmb(s.net_due_rmb)}</td>
                <td>{rmb(s.settlement_balance_rmb)}</td>
                <td>{s.audit_required ? <span className="pill pill-warn">需审计 / required</span> : <span className="pill pill-muted">不需 / no</span>}</td>
                <td><span className={`pill ${s.status === 'approved' ? 'pill-good' : s.status === 'audited' ? 'pill-warn' : 'pill-muted'}`}>{s.status}</span></td>
                <td>
                  <div className="row" style={{ gap: 5, flexWrap: 'wrap' }}>
                    {s.status !== 'approved' && <button disabled={busy} onClick={() => act(() => execActions.reconcileSettlement(s.id), '已核对 / reconciled')}>重新核对</button>}
                    {s.status === 'draft' && !!s.audit_required && <button className="btn-warn" disabled={busy} onClick={() => act(() => execActions.auditSettlement(s.id), '已审计 / audited')}>审计</button>}
                    {s.status !== 'approved' && <button className="btn-good" disabled={busy} onClick={() => act(() => execActions.approveSettlement(s.id), '已批准 / approved')}>批准</button>}
                    {s.status === 'approved' && <button className="btn-primary" disabled={busy} onClick={() => act(() => execActions.releaseSettlement(s.id), '结算款已释放 / released')}>释放结算款</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </Section>

      {/* st.15 acceptance — the submit→inspect→remediate quality loop (gates st.16/closeout) */}
      <AcceptanceSection pid={pid} scId={scId} />

      {/* st.16 GM final inspection — deterministic 终验 → three-way closeout → st.17 */}
      <FinalInspectionSection pid={pid} scId={scId} />

      {/* st.16 GM→PM remediation loop (Feature A) — photo-gated per-finding assign→resolve→verify→close */}
      <FinalInspectionLoopSection pid={pid} scId={scId} />

      {/* st.17 retention release — the final money event → project closed */}
      <Section title="质保金释放 / Retention release (§17)" action={data.retention_releases.length === 0
        ? <span className="row" style={{ gap: 6 }}>
            <input type="date" value={retDlp} onChange={(e) => setRetDlp(e.target.value)} style={{ width: 150 }} title="DLP 结束日 / DLP end" />
            <button className="btn-primary" disabled={busy} onClick={() => act(() => execActions.initRetention(scId, retDlp ? new Date(retDlp).toISOString() : null), '质保金释放已初始化 / initialized')}>初始化 / Initialize</button>
          </span>
        : undefined}>
        {data.retention_releases.length === 0 ? <div className="muted small">未初始化 / not initialized — 质保期满后释放。/ released after the DLP.</div> : (() => {
          const r = data.retention_releases[0];
          const released = r.status === 'released';
          return (
            <div className="row-wrap" style={{ alignItems: 'center' }}>
              <span className="muted small">金额（含税）/ amount (tax-incl.) {rmb(r.retention_amount_rmb)}</span>
              <span className={`pill ${released ? 'pill-good' : r.status === 'approved' ? 'pill-warn' : 'pill-muted'}`}>{r.status}</span>
              {r.dlp_ends_at && <span className="muted small">DLP 至 {r.dlp_ends_at.slice(0, 10)}</span>}
              {!released && (
                <>
                  {r.bd_check_favorable === 1
                    ? <span className="pill pill-good">BD 核查 ✓</span>
                    : <button disabled={busy} onClick={() => act(() => execActions.retentionBdCheck(r.id, true), 'BD 核查通过 / favorable')}>BD 核查通过</button>}
                  <label className="small row" style={{ gap: 4 }}><input type="checkbox" style={{ width: 'auto' }} checked={gmOk} onChange={(e) => setGmOk(e.target.checked)} />GM 批准</label>
                  <label className="small row" style={{ gap: 4 }}><input type="checkbox" style={{ width: 'auto' }} checked={dlpOk} onChange={(e) => setDlpOk(e.target.checked)} />DLP 已满</label>
                  <button className="btn-good" disabled={busy} onClick={() => act(async () => { const res: any = await execActions.releaseRetention(r.id, gmOk, dlpOk); if (!res.released) setMsg({ kind: 'err', text: `未释放（状态 ${res.status}）— 需 BD 满意 + GM 批准 + DLP 已满三项齐备 / needs all three` }); }, '质保金已释放 · 项目关闭 / released · project closed')}>释放质保金 / Release</button>
                </>
              )}
              {released && <span className="muted small">项目已关闭 / project closed</span>}
            </div>
          );
        })()}
      </Section>

      {/* calendar alerts */}
      <Section title={`日历提醒 / Calendar alerts (${data.calendar_alerts.length})`}>
        {data.calendar_alerts.length === 0 ? <div className="muted small">—</div> : (
          <div className="cmp-wrap"><table className="cmp"><thead><tr>
            <th>类型 / kind</th><th>到期 / due</th><th>提醒 / fire (T-7)</th><th>状态</th>
          </tr></thead><tbody>
            {data.calendar_alerts.map((a) => (
              <tr key={a.id}>
                <td>{ALERT_KIND_LABEL[a.kind] ?? a.kind}</td>
                <td>{a.due_at ? a.due_at.slice(0, 10) : '—'}</td>
                <td className="muted">{a.fire_at ? a.fire_at.slice(0, 10) : '—'}</td>
                <td><span className="pill pill-muted">{a.status}</span></td>
              </tr>
            ))}
          </tbody></table></div>
        )}
      </Section>
    </div>
  );
}

/**
 * st.15 验收 / Acceptance — the deterministic submit→inspect→remediate loop. Per criterion: PM logs a
 * finding (→ remediation), labor submits evidence, PM re-inspects (mark met / fail). All met|waived → the
 * round passes (server advances the package to st.16); the iteration cap escalates a criterion to the GM,
 * which a GM waiver (§15c) resolves. Self-contained: its own query + actions (re-derived server-side).
 */
function AcceptanceSection({ pid, scId }: { pid: string; scId: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const { data, isLoading } = useQuery<AcceptanceSummary>({ queryKey: ['acceptance', scId], queryFn: () => getAcceptance(scId) });

  async function act(fn: () => Promise<any>, okText = '已更新 / done') {
    setBusy(true); setMsg(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['acceptance', scId] });
      await qc.invalidateQueries({ queryKey: ['subcontracts', pid] }); // passing advances the package stage
      setMsg({ kind: 'ok', text: okText });
    } catch (e: any) {
      setMsg({ kind: 'err', text: e?.body?.error ?? e?.message ?? '操作失败 / failed' });
    } finally { setBusy(false); }
  }

  if (isLoading) return <Section title="验收 / Acceptance (§15)"><Spinner label="加载中… / Loading…" /></Section>;
  const criteria = data?.criteria ?? [];
  const card = data?.scorecard ?? null;
  const findings = data?.findings ?? [];

  const overallPill = card && (card.overall_status === 'passed' ? <span className="pill pill-good">验收通过 / passed → st.16</span>
    : card.overall_status === 'escalated' ? <span className="pill pill-bad">已升级 GM / escalated</span>
    : <span className="pill pill-stage">进行中 / in&nbsp;progress · 第 {card.acceptance_round} 轮</span>);

  return (
    <Section title="验收 / Acceptance (§15)" action={overallPill ?? (criteria.length > 0
      ? <button className="btn-primary" disabled={busy} onClick={() => act(() => acceptanceActions.open(scId), '验收已开始 / round opened')}>开始验收 / Open round</button>
      : undefined)}>
      {msg && <div className="small" style={{ color: msg.kind === 'err' ? 'var(--bad)' : 'var(--good)', marginBottom: 6 }}>{msg.text}</div>}
      {criteria.length === 0
        ? <div className="muted small">尚无验收标准 / no acceptance criteria — 先在 st.13 拟定标准。/ author criteria (st.13) first.</div>
        : !card
          ? <div className="muted small">{criteria.length} 项标准已就绪 / {criteria.length} criteria ready — 点击「开始验收」启动检查。/ open a round to begin.</div>
          : (
            <div className="cmp-wrap"><table className="cmp"><thead><tr>
              <th>验收项 / criterion</th><th>状态 / status</th><th>轮次 / iter</th><th>操作 / actions</th>
            </tr></thead><tbody>
              {criteria.map((c) => {
                const st = card.states.find((s) => s.criterion_id === c.id);
                if (!st) return null;
                const sm = CRITERION_STATUS[st.status] ?? { label: st.status, pill: 'pill-muted' };
                const openFinding = findings.filter((f) => f.criterion_id === c.id && f.status === 'in_remediation').slice(-1)[0];
                const closed = st.status === 'met' || st.status === 'waived';
                return (
                  <tr key={c.id}>
                    <td>{c.label_zh ?? c.label_en ?? c.id}{c.is_critical ? <span className="pill pill-bad small" style={{ marginLeft: 6 }}>关键</span> : null}</td>
                    <td><span className={`pill ${sm.pill}`}>{sm.label}</span>{st.escalated && st.status !== 'met' && st.status !== 'waived' ? <span className="pill pill-bad small" style={{ marginLeft: 4 }}>GM</span> : null}</td>
                    <td className="muted">{st.iteration}/{card.max_acceptance_iterations}</td>
                    <td>
                      <div className="row" style={{ gap: 5, flexWrap: 'wrap' }}>
                        {closed ? <span className="muted small">—</span> : <>
                          {st.status === 'pending' && <button disabled={busy} onClick={() => { const n = window.prompt('问题描述 / finding note:'); if (n && n.trim()) act(() => acceptanceActions.recordFinding(card.id, c.id, n.trim()), '问题已记录 / finding logged'); }}>记录问题</button>}
                          {st.status === 'in_remediation' && openFinding && <button disabled={busy} onClick={() => { const n = window.prompt('整改说明（必填）/ remediation note (required):'); if (n == null) return; if (!n.trim()) { setMsg({ kind: 'err', text: '整改需附说明 / remediation needs a note' }); return; } act(() => acceptanceActions.remediate(openFinding.id, n.trim()), '整改已提交 / remediation submitted'); }}>提交整改</button>}
                          {st.status === 'in_remediation' && <button disabled={busy} onClick={() => act(() => acceptanceActions.failReInspection(card.id, c.id), '复检不合格 / re-inspection failed')}>复检不合格</button>}
                          <button className="btn-good" disabled={busy} onClick={() => act(async () => { const r: any = await acceptanceActions.markMet(card.id, c.id); if (r.overall_status === 'passed') setMsg({ kind: 'ok', text: '全部合格 · 验收通过 → st.16 / all met · passed' }); }, '已标记合格 / met')}>标记合格</button>
                          {st.escalated && <button className="btn-warn" disabled={busy} onClick={() => { const n = window.prompt('GM 豁免理由 / GM waiver note:'); if (n && n.trim()) act(() => acceptanceActions.waive(card.id, c.id, n.trim()), 'GM 已豁免 / waived'); }}>GM 豁免</button>}
                        </>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody></table></div>
          )}
    </Section>
  );
}

/**
 * st.16 终验 / GM final inspection — the DETERMINISTIC, key-free manual path. The GM logs findings (severity +
 * a 「PM 漏检」flag + optional linked criterion); a `blocking` finding fails (stays at st.16), else it passes →
 * the V10 three-way closeout writes (reputation always; pm_record + crm on gm-missed) and the package advances
 * 16→17. The LLM aesthetic-lens gate is the optional AI-assist (live-eval pass) — not wired into this form.
 */
function FinalInspectionSection({ pid, scId }: { pid: string; scId: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [gmAttendee, setGmAttendee] = useState('');
  const [pmName, setPmName] = useState('');
  const [draft, setDraft] = useState<FinalInspectionFinding[]>([]);
  const { data, isLoading } = useQuery<FinalInspectionSummary>({ queryKey: ['final-inspection', scId], queryFn: () => getFinalInspection(scId) });

  if (isLoading) return <Section title="终验 / GM final inspection (§16)"><Spinner label="加载中… / Loading…" /></Section>;
  const fi = data?.final_inspection ?? null;
  const criteria = data?.criteria ?? [];
  const passed = fi?.status === 'passed';

  function addFinding() { setDraft((d) => [...d, { label_md: '', severity: 'minor', gm_missed_by_pm: false, related_criterion_id: '' }]); }
  function patch(i: number, p: Partial<FinalInspectionFinding>) { setDraft((d) => d.map((f, idx) => (idx === i ? { ...f, ...p } : f))); }
  function remove(i: number) { setDraft((d) => d.filter((_, idx) => idx !== i)); }

  async function submit() {
    setBusy(true); setMsg(null);
    try {
      const findings = draft.filter((f) => f.label_md.trim());
      const r = await finalInspectionActions.record(scId, { gmAttendee: gmAttendee || undefined, pmName: pmName || undefined, findings });
      await qc.invalidateQueries({ queryKey: ['final-inspection', scId] });
      await qc.invalidateQueries({ queryKey: ['subcontracts', pid] });
      setDraft([]);
      const legs = [r.three_way.reputationCaptured && '信誉', r.three_way.pmRecordWritten && 'PM 记录', r.three_way.crmRecordWritten && 'CRM'].filter(Boolean).join(' + ');
      setMsg({ kind: r.passed ? 'ok' : 'err', text: r.passed
        ? `终验通过 / passed → st.17 · 已写入 ${legs}${r.gm_missed_count ? ` · PM 漏检 ${r.gm_missed_count} 项` : ''}`
        : `存在阻断项 / blocking finding — 未通过，停留 st.16（已记录 ${legs}）` });
    } catch (e: any) {
      setMsg({ kind: 'err', text: e?.body?.error ?? e?.message ?? '操作失败 / failed' });
    } finally { setBusy(false); }
  }

  const statusPill = fi
    ? (passed ? <span className="pill pill-good">已终验 / passed → st.17</span> : <span className="pill pill-warn">有阻断项 / blocking — 待复验</span>)
    : <span className="pill pill-muted">未终验 / not inspected</span>;

  return (
    <Section title="终验 / GM final inspection (§16)" action={statusPill}>
      {msg && <div className="small" style={{ color: msg.kind === 'err' ? 'var(--bad)' : 'var(--good)', marginBottom: 6 }}>{msg.text}</div>}

      {/* the recorded inspection (latest) */}
      {fi && (
        <div className="card" style={{ background: 'var(--paper)', marginBottom: 8 }}>
          <div className="row-wrap" style={{ marginBottom: fi.findings.length ? 6 : 0 }}>
            <span className={`pill ${passed ? 'pill-good' : 'pill-warn'}`}>{fi.status}</span>
            {fi.gm_attendee && <span className="muted small">GM {fi.gm_attendee}</span>}
            {fi.inspected_at && <span className="muted small">{fi.inspected_at.slice(0, 10)}</span>}
          </div>
          {fi.findings.length > 0 && (
            <ul className="small" style={{ margin: 0, paddingLeft: 18 }}>
              {fi.findings.map((f, i) => (
                <li key={i}>
                  {f.label_md} <span className={`pill ${SEVERITY_LABEL[f.severity]?.pill ?? 'pill-muted'} small`}>{SEVERITY_LABEL[f.severity]?.label ?? f.severity}</span>
                  {f.gm_missed_by_pm && <span className="pill pill-bad small" style={{ marginLeft: 4 }}>PM 漏检</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* the entry form — hidden once a passed inspection is the closeout record */}
      {passed ? <div className="muted small">终验已通过，作为竣工记录归档。/ closeout record archived.</div> : (
        <div>
          <div className="row-wrap" style={{ alignItems: 'flex-end', marginBottom: 8 }}>
            <div className="field" style={{ width: 180, marginBottom: 0 }}><label>总经理 / GM attendee</label><input value={gmAttendee} onChange={(e) => setGmAttendee(e.target.value)} placeholder="终验出席人" /></div>
            <div className="field" style={{ width: 180, marginBottom: 0 }}><label>项目经理 / PM（漏检归属）</label><input value={pmName} onChange={(e) => setPmName(e.target.value)} placeholder="可留空 / optional" /></div>
            <button disabled={busy} onClick={addFinding}>＋ 添加问题 / Add finding</button>
          </div>
          {draft.map((f, i) => (
            <div key={i} className="row-wrap" style={{ alignItems: 'flex-end', marginBottom: 6 }}>
              <div className="field" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}><label>问题 / finding</label><input value={f.label_md} onChange={(e) => patch(i, { label_md: e.target.value })} placeholder="例：吊顶接缝不平" /></div>
              <div className="field" style={{ width: 130, marginBottom: 0 }}>
                <label>严重度 / severity</label>
                <select value={f.severity} onChange={(e) => patch(i, { severity: e.target.value as FindingSeverity })}>
                  <option value="minor">轻微 / minor</option>
                  <option value="major">一般 / major</option>
                  <option value="blocking">阻断 / blocking</option>
                </select>
              </div>
              <div className="field" style={{ width: 170, marginBottom: 0 }}>
                <label>相关标准 / criterion</label>
                <select value={f.related_criterion_id} onChange={(e) => patch(i, { related_criterion_id: e.target.value })}>
                  <option value="">（无 / none）</option>
                  {criteria.map((c) => <option key={c.id} value={c.id}>{c.label_zh ?? c.label_en ?? c.id}</option>)}
                </select>
              </div>
              <label className="small row" style={{ gap: 4, marginBottom: 6 }}><input type="checkbox" style={{ width: 'auto' }} checked={f.gm_missed_by_pm} onChange={(e) => patch(i, { gm_missed_by_pm: e.target.checked })} />PM 漏检</label>
              <button className="btn-danger" disabled={busy} onClick={() => remove(i)}>移除</button>
            </div>
          ))}
          <div className="row" style={{ marginTop: 4 }}>
            <button className="btn-primary" disabled={busy} onClick={submit} title="无阻断项即通过；记录将写入信誉/PM/CRM 三处">
              {draft.some((f) => f.label_md.trim()) ? '提交终验（按问题裁决）/ Submit' : '通过终验（无问题）/ Pass (no findings)'}
            </button>
            <span className="muted small" style={{ marginLeft: 8 }}>无阻断项即通过 → 进入 st.17 质保金。/ no blocking finding → passes to st.17 retention.</span>
          </div>
        </div>
      )}
    </Section>
  );
}

/** Feature A · st.16 GM→PM remediation loop. Photo-gated per-finding flow: GM logs (photo) + assigns a roster
 *  PM → PM acknowledges + resolves (photo) → GM verifies (proof photo + satisfaction) → closes. An
 *  unsatisfactory/waived outcome is written to the labor company's reputation record. Coexists with the
 *  one-shot 终验 above (whichever passes first locks the other). Role-gated: only the GM sees the controls. */
function FinalInspectionLoopSection({ pid, scId }: { pid: string; scId: string }) {
  const qc = useQueryClient();
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: getMe });
  const isGm = me?.account?.role === 'gm';
  const { data: loop, isLoading } = useQuery<FinalInspectionLoop>({ queryKey: ['fi-loop', scId], queryFn: () => getFinalInspectionLoop(scId) });
  const { data: summary } = useQuery<FinalInspectionSummary>({ queryKey: ['final-inspection', scId], queryFn: () => getFinalInspection(scId) });
  // GM assigns findings to a real PM — placeholders (共用测试账号) excluded, same as the standards 指派 picker
  const { data: pms } = useQuery<StaffRow[]>({ queryKey: ['staff', 'site_pm', 'assignable'], queryFn: () => listStaff('site_pm', { excludePlaceholders: true }), enabled: !!isGm });

  const criteria = summary?.criteria ?? [];
  const findings = loop?.findings ?? [];

  const [label, setLabel] = useState('');
  const [severity, setSeverity] = useState<FindingSeverity>('major');
  const [criterion, setCriterion] = useState('');
  const [gmMissed, setGmMissed] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  async function logFinding() {
    if (!file) { setMsg({ kind: 'err', text: '问题项必须上传照片 / a photo is required' }); return; }
    if (!label.trim()) { setMsg({ kind: 'err', text: '请填写问题描述 / describe the finding' }); return; }
    setBusy(true); setMsg(null);
    try {
      const fd = new FormData();
      fd.append('label_md', label); fd.append('severity', severity);
      fd.append('related_criterion_id', criterion); fd.append('gm_missed_by_pm', gmMissed ? 'true' : 'false');
      fd.append('photo', file);
      await finalInspectionLoopActions.logFinding(scId, fd);
      setLabel(''); setFile(null); setGmMissed(false);
      await qc.invalidateQueries({ queryKey: ['fi-loop', scId] });
      setMsg({ kind: 'ok', text: '问题已记录 / finding logged' });
    } catch (e: any) { setMsg({ kind: 'err', text: e?.body?.detail ?? e?.body?.error ?? e?.message ?? '失败' }); }
    finally { setBusy(false); }
  }

  if (isLoading) return <Section title="终验整改闭环 / GM→PM remediation loop (§16)"><Spinner label="加载中… / Loading…" /></Section>;

  const openCount = findings.filter((f) => f.status !== 'closed' && f.status !== 'waived').length;
  const statusPill = loop?.status === 'passed'
    ? <span className="pill pill-good">已闭环 / closed → st.17</span>
    : findings.length ? <span className="pill pill-warn">{openCount} 项进行中 / open</span>
      : <span className="pill pill-muted">无整改项 / none</span>;

  return (
    <Section title="终验整改闭环 / GM→PM remediation loop (§16)" action={statusPill}>
      {msg && <div className="small" style={{ color: msg.kind === 'err' ? 'var(--bad)' : 'var(--good)', marginBottom: 6 }}>{msg.text}</div>}
      <div className="muted small" style={{ marginBottom: 8 }}>每个问题项必须附照片（记录/整改/复核三处）；GM 指派项目经理整改，复核“不满意”或豁免将写入劳务公司信誉档案。/ photo required at log, resolve & verify; an unsatisfactory or waived outcome is written to the labor company's record.</div>

      {findings.map((f) => <LoopFindingRow key={f.id} f={f} isGm={!!isGm} pms={pms ?? []} scId={scId} pid={pid} />)}

      {isGm && loop?.status !== 'passed' && (
        <div className="card" style={{ background: 'var(--paper)', marginTop: 8 }}>
          <div className="row-wrap" style={{ alignItems: 'flex-end', gap: 8 }}>
            <div className="field" style={{ flex: 1, minWidth: 160, marginBottom: 0 }}><label>问题 / finding</label><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="例：吊顶接缝不平" /></div>
            <div className="field" style={{ width: 130, marginBottom: 0 }}><label>严重度 / severity</label><select value={severity} onChange={(e) => setSeverity(e.target.value as FindingSeverity)}><option value="minor">轻微 / minor</option><option value="major">一般 / major</option><option value="blocking">阻断 / blocking</option></select></div>
            <div className="field" style={{ width: 160, marginBottom: 0 }}><label>相关标准 / criterion</label><select value={criterion} onChange={(e) => setCriterion(e.target.value)}><option value="">（无 / none）</option>{criteria.map((c) => <option key={c.id} value={c.id}>{c.label_zh ?? c.label_en ?? c.id}</option>)}</select></div>
            <label className="small row" style={{ gap: 4, marginBottom: 6 }}><input type="checkbox" style={{ width: 'auto' }} checked={gmMissed} onChange={(e) => setGmMissed(e.target.checked)} />PM 漏检</label>
            <div className="field" style={{ marginBottom: 0 }}><label>问题照片（必填）/ photo</label><input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></div>
            <button className="btn-primary" disabled={busy} onClick={logFinding}>记录问题 / Log finding</button>
          </div>
        </div>
      )}
      {!me?.authenticated && <div className="muted small">登录（GM）后可记录问题并指派整改 / log in as GM to act.</div>}
    </Section>
  );
}

function LoopFindingRow({ f, isGm, pms, scId, pid }: { f: LoopFinding; isGm: boolean; pms: StaffRow[]; scId: string; pid: string }) {
  const qc = useQueryClient();
  const [pmSel, setPmSel] = useState(f.assigned_pm_staff_id ?? '');
  const [proof, setProof] = useState<File | null>(null);
  const [verdict, setVerdict] = useState<'satisfactory' | 'unsatisfactory'>('satisfactory');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true); setErr(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: ['fi-loop', scId] });
      await qc.invalidateQueries({ queryKey: ['subcontracts', pid] });
    } catch (e: any) { setErr(e?.body?.detail ?? e?.body?.error ?? e?.message ?? '失败'); }
    finally { setBusy(false); }
  }

  const st = LOOP_STATUS[f.status];
  const sev = SEVERITY_LABEL[f.severity];
  const done = f.status === 'closed' || f.status === 'waived';

  return (
    <div className="card" style={{ marginBottom: 6 }}>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'center', marginBottom: 6 }}>
        <strong>{f.label_md}</strong>
        {sev && <span className={`pill ${sev.pill} small`}>{sev.label}</span>}
        {st && <span className={`pill ${st.pill} small`}>{st.label}</span>}
        {f.gm_missed_by_pm ? <span className="pill pill-bad small">PM 漏检</span> : null}
        {f.gm_satisfaction === 'unsatisfactory' && <span className="pill pill-bad small">不满意 / unsatisfactory</span>}
        {f.recorded_to_labor_record ? <span className="pill pill-warn small">已记入信誉档案 / on record</span> : null}
      </div>
      <div className="row-wrap small" style={{ gap: 12, marginBottom: isGm && !done ? 8 : 0 }}>
        <a href={loopPhotoUrl(f.id, 'finding')} target="_blank" rel="noreferrer">问题照片 ↗</a>
        {f.resolution_evidence_path && <a href={loopPhotoUrl(f.id, 'pm')} target="_blank" rel="noreferrer">整改照片 ↗</a>}
        {f.gm_proof_path && <a href={loopPhotoUrl(f.id, 'gm')} target="_blank" rel="noreferrer">复核照片 ↗</a>}
        {f.promised_remediation_date && <span className="muted">承诺完成 / promised {f.promised_remediation_date}</span>}
      </div>

      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 4 }}>{err}</div>}

      {isGm && !done && (
        <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end', marginTop: 6 }}>
          {(f.status === 'open' || f.status === 'assigned') && (
            <>
              <div className="field" style={{ width: 200, marginBottom: 0 }}><label>指派项目经理 / assign PM</label>
                <select value={pmSel} onChange={(e) => setPmSel(e.target.value)}><option value="">— 选择 / select —</option>{pms.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
              <button disabled={busy || !pmSel} onClick={() => run(() => finalInspectionLoopActions.assign(f.id, pmSel))}>指派 / Assign</button>
            </>
          )}
          {f.status === 'pm_resolved' && (
            <>
              <div className="field" style={{ width: 150, marginBottom: 0 }}><label>复核结论 / verdict</label>
                <select value={verdict} onChange={(e) => setVerdict(e.target.value as 'satisfactory' | 'unsatisfactory')}><option value="satisfactory">满意 / satisfactory</option><option value="unsatisfactory">不满意 / unsatisfactory</option></select></div>
              <div className="field" style={{ marginBottom: 0 }}><label>复核照片（必填）/ proof</label><input type="file" accept="image/*" onChange={(e) => setProof(e.target.files?.[0] ?? null)} /></div>
              <button className="btn-primary" disabled={busy} onClick={() => { if (!proof) { setErr('复核必须上传照片 / a proof photo is required'); return; } const fd = new FormData(); fd.append('gm_satisfaction', verdict); fd.append('photo', proof); void run(() => finalInspectionLoopActions.verify(f.id, fd)); }}>复核闭环 / Verify</button>
            </>
          )}
          {f.severity !== 'blocking' && (
            <button disabled={busy} onClick={() => run(() => finalInspectionLoopActions.waive(f.id, 'GM 豁免 / waived'))}>豁免 / Waive</button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 付款计划（含税）/ Payment plan — 改改 9/20 (fb_3267bf3b), reshaped 改改 9/21.
 *
 * 9/20 「能否按照上传的签订版合同…每次的付款比例及金额」: the plan existed server-side all along
 * (payments/schedule.ts) but nothing in the UI could change it — 「✎ 编辑付款批次」 is that edit.
 *
 * 9/21 「付款比例不对，几笔款总合计不能超合同额」: the table listed each row's PLANNED figure — 预付款 30% ¥52,200,
 * 竣工验收 100% ¥174,001, 质保金 5% ¥8,700 — which reads as 135% of the contract, because the 100% is the gross
 * 计量 the advance is recovered from and the retention withheld from. It now lists what each row actually
 * PAYS (net_payable_rmb / payable_pct), says how a batch nets down, and the 合计 row proves Σ = 合同额.
 *
 * Money rule: every 金额 rendered here is a number the SERVER computed (含税 when the contract carries a
 * 税率). Nothing multiplies by a rate in the browser — with no 税率 the server sends nulls and we say so,
 * in the same warn wording ContractValueBlock uses.
 */
function PaymentPlanSection({ scId, data, busy, act, role }: {
  scId: string;
  data: ExecutionSummary;
  busy: boolean;
  act: (fn: () => Promise<any>, okText?: string) => Promise<void>;
  /** me.account.role — a SIGNED plan is 预算/GM only (the server re-checks); a draft is any money role. */
  role: string | null | undefined;
}) {
  const [editing, setEditing] = useState(false);
  // 改改 9/21 (fb_9bc4f320) · 从签订版合同读取付款方式: the AI reading that prefills the editor (nothing is saved
  // until 保存), its busy clock, and its error. `editorKey` remounts the editor so a new reading re-seeds it.
  const [reading, setReading] = useState<PaymentTermsExtractResult | null>(null);
  const [extracting, setExtracting] = useState(false);
  const [extractSecs, setExtractSecs] = useState(0);
  const [extractErr, setExtractErr] = useState<string | null>(null);
  // review R10: a reading with no stages and no 结算 stage found nothing to plan — said, never opened as an empty plan
  const [emptyReading, setEmptyReading] = useState<PaymentTermsExtractResult | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const sc = data.subcontract as SubcontractRow & Record<string, unknown>;
  const mode = planModeOf(data);
  const installment = mode === 'installment';
  const schedule = data.payment_schedule ?? null;
  const rows = schedule?.rows ?? [];
  const sum = schedule?.summary ?? null;
  const hasTax = sc.final_tax_pct != null;
  const settled = data.settlements.length > 0;
  // Once the paper is signed, amending the batches is a 预算/GM decision — the same allow-list the server
  // puts on PUT /payment-plan for a signed subcontract ('agent' so the browser rig can drive it). A DRAFT
  // plan stays editable by any money role on this page, like AdvanceTermsEditor.
  const signed = sc.status === 'signed';
  const canEdit = !signed || PLAN_AMEND_ROLES.has(role ?? '');

  /** Milestones that already carry a progress cert — their % is frozen (the server refuses to move it). */
  const certifiedMilestones = new Set(
    data.payment_certs.filter((c) => c.cert_kind === 'progress' && c.milestone_id).map((c) => c.milestone_id as string),
  );

  /** The cert raised against a schedule row, if any — its state pill is the row's 状态. */
  function certFor(r: PaymentScheduleRow) {
    if (r.kind === 'advance') return data.payment_certs.find((c) => c.cert_kind === 'advance') ?? null;
    if (r.kind === 'progress') return data.payment_certs.find((c) => c.cert_kind === 'progress' && c.milestone_id === r.milestone_id) ?? null;
    return null;
  }

  const progressRows = rows.filter((r) => r.kind === 'progress');
  // Σ 占合同额 % — percentages only; the money total beside it is the server's total_payable_rmb.
  const payablePctSum = rows.some((r) => r.payable_pct != null) ? round2(rows.reduce((t, r) => t + (r.payable_pct ?? 0), 0)) : null;
  // 改改 9/22: 质保金 5% is the company standard — retention_compliant (≤ 3%) no longer raises a warning here.
  const flagged = sum != null && (!sum.balanced || sum.has_unset_pcts || !sum.pcts_in_range || sum.any_negative_net);
  const settleTo = sum?.settle_to_pct ?? sc.settlement_stage?.settle_to_pct ?? null;
  const estimate = sum?.settlement_is_estimate !== false;   // the 结算 row is an estimate until a settlement fixes it
  // the mode may switch only while nothing has been paid against the plan (the server 409s otherwise)
  const hasProgressCert = data.payment_certs.some((c) => c.cert_kind === 'progress');
  const modeSwitchable = planModeKnown(data) && !hasProgressCert;
  const scan = data.executed_file ?? sc.executed_file ?? null;
  const canExtract = !settled && !!scan && PLAN_AMEND_ROLES.has(role ?? '');

  async function readContract() {
    setExtracting(true); setExtractErr(null); setExtractSecs(0); setEmptyReading(null);
    const t0 = Date.now();
    const tick = window.setInterval(() => setExtractSecs(Math.round((Date.now() - t0) / 1000)), 1000);
    try {
      const result = await execActions.extractPaymentTerms(scId);
      if (result.proposal.milestones.length === 0 && !result.proposal.settlement_stage) {
        setEmptyReading(result);   // keep the current mode and plan; she enters the batches by hand
        return;
      }
      setReading(result);
      setEditorKey((k) => k + 1);
      setEditing(true);
    } catch (e) {
      // 409 no_executed_scan / 415 / 413 / 503 all carry a bilingual `detail`
      setExtractErr(errText(e));
    } finally {
      window.clearInterval(tick);
      setExtracting(false);
    }
  }
  function closeEditor() { setEditing(false); setReading(null); }

  return (
    <Section
      title="付款计划（含税）/ Payment plan (tax-inclusive)"
      action={(canExtract || !settled) ? (
        <span className="row-wrap" style={{ gap: 6, justifyContent: 'flex-end' }}>
          {/* 改改 9/21 (fb_9bc4f320) 「能否系统直接识别合同里的付款方式？」 — read it off the executed scan; she reviews, then 保存. */}
          {canExtract && (
            <button className="small" disabled={busy || extracting} onClick={() => void readContract()}
              title={`AI 读取签订版合同扫描件（${scan!.file_name}）中的付款条款，预填到付款批次；保存前不会改动任何数据 / reads the payment clauses off the executed scan and prefills the batches — nothing is saved until you press 保存`}>
              {extracting
                ? <Spinner size={12} label={`正在读取签订版合同（约 1–3 分钟）… ${extractSecs}s / reading the signed contract (1–3 min)…`} />
                : '🤖 从签订版合同读取付款方式 / Read from the signed contract'}
            </button>
          )}
          {!settled && !editing && canEdit && (
            <button className="small" disabled={busy || extracting}
              title={signed
                ? '合同已签署 — 修改批次需填写原因，并记入审计留痕 / the subcontract is signed: amending the batches needs a reason and is audited'
                : '把进度款拆成多期：每期填名称与本期实付占合同额 % / split the progress payment into batches — a name and its cash % of the contract each'}
              onClick={() => { setReading(null); setEmptyReading(null); setEditing(true); }}>✎ 编辑付款批次 / Edit batches</button>
          )}
          {!settled && !editing && !canEdit && signed && (
            <span className="muted small" title="合同签署后，付款批次只能由预算或总经理修改 / once signed, only 预算 or the GM may amend the batches">
              付款批次由预算/总经理修改 / amended by 预算 or the GM
            </span>
          )}
        </span>
      ) : undefined}
    >
      {extractErr && <div className="small" style={{ color: 'var(--bad)', marginBottom: 6, wordBreak: 'break-word' }}>读取失败 / could not read: {extractErr}</div>}
      {emptyReading && (
        <div className="small" style={{ color: 'var(--warn)', marginBottom: 6, wordBreak: 'break-word' }}>
          ⚠ 未在签订版合同中识别到付款条款，请手工填写（点「✎ 编辑付款批次」）。/ No payment terms were recognised in the signed contract — enter the batches by hand.
          {emptyReading.proposal.notes.length > 0 && (
            <ul className="muted" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {emptyReading.proposal.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          )}
        </div>
      )}
      {!hasTax && <div className="small" style={{ color: 'var(--warn)', marginBottom: 6 }}>⚠ {TAX_PCT_MISSING_HINT}</div>}

      {rows.length === 0
        ? <div className="muted small">尚无付款计划 / no payment plan yet — 点「✎ 编辑付款批次」按合同约定分期。/ use ✎ Edit batches to enter the batches the contract sets.</div>
        : (
          <>
            <div className="cmp-wrap"><table className="cmp"><thead><tr>
              <th>类型 / kind</th><th>付款节点 / milestone</th><th>占合同额 % / of contract</th>
              <th>实付金额（含税）/ payable (tax-incl.)</th><th>说明 / how</th><th>状态 / status</th>
            </tr></thead><tbody>
              {rows.map((r) => {
                const cert = certFor(r);
                // 改改 9/21 (fb_9cf4aaea) · 按节点支付: the 结算 row is an estimate on the contract value until the
                // settlement fixes the 结算额, and the advance is her contract's 首期款.
                const isSettle = installment && r.kind === 'settlement';
                // review R7: once a settlement exists the server sends the actual 结算款 and stops calling it an estimate
                const actual = isSettle && !estimate && r.settlement_actual_rmb != null ? r.settlement_actual_rmb : null;
                const est = isSettle && estimate ? '（预估）' : '';
                const kindLabel = installment && r.kind === 'advance' ? '首期款' : CERT_KIND_LABEL[r.kind] ?? r.kind;
                const settlement = r.kind === 'settlement' ? data.settlements[0] ?? null : null;
                return (
                  <tr key={r.id}>
                    <td>{kindLabel}</td>
                    <td className="cell-wrap">
                      {installment && r.kind === 'advance'
                        ? '首期款 · 签订后支付 / first payment · on signing'
                        : isSettle
                          ? <>
                              结算 · 支付至结算额 {settleTo != null ? `${fmtPct(settleTo)}%` : '—'}{estimate ? '（预估，以结算为准）' : ''}
                              {r.label_zh && r.label_zh !== SETTLEMENT_LABEL_DEFAULT && <div className="muted small">{r.label_zh}</div>}
                            </>
                          : r.label_zh ?? r.label_en ?? (r.milestone_id ?? '—')}
                    </td>
                    <td>{r.payable_pct != null ? `${fmtPct(r.payable_pct)}%${est}` : r.planned_pct == null ? <span className="muted">未设定 / unset</span> : '—'}</td>
                    <td style={{ wordBreak: 'break-word' }}>
                      {actual != null
                        ? <>结算款 · 实际 {rmbCents(actual)}<span className="muted">（预估 {rmbCents(r.planned_amount_rmb ?? r.net_payable_rmb)}）</span></>
                        : <>{rmbCents(r.net_payable_rmb)}{r.net_payable_rmb != null ? est : ''}</>}
                    </td>
                    <td className="muted cell-wrap">{payableHow(r, mode)}</td>
                    <td>
                      {cert
                        ? <StatePill state={cert.state} />
                        : settlement
                          ? <span className={`pill ${settlement.status === 'approved' ? 'pill-good' : 'pill-muted'}`}>{settlement.status}</span>
                          : r.kind === 'progress' || r.kind === 'advance'
                            ? <span className="pill pill-muted">未申请 / not requested</span>
                            : r.kind === 'settlement'
                              ? <span className="pill pill-muted">未起草 / not drafted</span>
                              : <span className="muted">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot><tr>
              <td colSpan={2}>合计 / total</td>
              <td>{payablePctSum != null ? `${fmtPct(payablePctSum)}%` : '—'}</td>
              <td>
                {rmbCents(sum?.total_payable_rmb)}
                {sum?.payable_matches_contract === true && <span className="pill pill-good" style={{ marginLeft: 6 }}>= 合同额 ✓</span>}
                {sum?.payable_matches_contract === false && (
                  <span className="pill pill-warn" style={{ marginLeft: 6 }}
                    title="各期实付合计与合同额（含税）不符 — 请核对付款批次 / the payments do not add up to the contract value — check the batches">
                    ≠ 合同额 {rmbCents(sum.contract_value_basis_rmb)} / does not match
                  </span>
                )}
              </td>
              <td colSpan={2} />
            </tr></tfoot></table></div>
            <div className="muted small" style={{ marginTop: 6 }}>{installment ? installmentNote(settleTo) : PLAN_NETTING_NOTE}</div>
          </>
        )}

      {/* 改改 9/21 · the gross subtotals that used to sit here (进度款合计 ¥174,001 …) are exactly what read as
          135% — the 说明 column carries the same figures per batch, so only the warnings remain. */}
      {sum && flagged && (
        <div className="row-wrap small" style={{ marginTop: 8, alignItems: 'center' }}>
          {!sum.balanced && (installment
            // installment: the server's `balanced` = 首期款 + 各期 + 结算 + 质保金 = 100, i.e. 付至 % + 质保 % = 100
            ? <span className="pill pill-warn" title="结算付至 % 与质保金 % 合计应为 100%">结算付至 {settleTo != null ? fmtPct(settleTo) : '—'}% + 质保金 {sc.retention_pct ?? 0}% ≠ 100%，请核对 / settle-to + retention ≠ 100% — check them</span>
            : <span className="pill pill-warn" title="各期计量比例合计不等于 100%">进度款各期未配平，请编辑付款批次 / the batches do not add up — edit them</span>)}
          {sum.has_unset_pcts && <span className="pill pill-warn">有未设定比例 / some % unset</span>}
          {!sum.pcts_in_range && <span className="pill pill-bad">比例超出 0–100 / % out of range</span>}
          {sum.any_negative_net && (installment
            ? <span className="pill pill-warn">首期款与各期已超过结算付至比例 / the stages exceed the settle-to %</span>
            : <span className="pill pill-warn">某期净额为负（抵扣+质保金过大）/ a batch nets below zero</span>)}
        </div>
      )}

      {settled && <div className="muted small" style={{ marginTop: 6 }}>已起草结算，付款批次已锁定。/ a settlement exists — the batches are locked.</div>}

      {editing && !settled && canEdit && reading && (
        <ContractReadingSummary reading={reading} advancePct={sc.advance_pct ?? null} retentionPct={sc.retention_pct ?? null}
          currentMode={mode} modeSwitchable={modeSwitchable}
          skipped={readingAfterLocked(reading.proposal.milestones,
            progressRows.filter((r) => !!r.milestone_id && certifiedMilestones.has(r.milestone_id))).skipped.map((m) => m.label_zh)} />
      )}
      {editing && !settled && canEdit && (
        <PaymentPlanEditor
          key={editorKey}
          scId={scId}
          signed={signed}
          rows={progressRows}
          certified={certifiedMilestones}
          advancePct={sc.advance_pct ?? null}
          retentionPct={sc.retention_pct ?? null}
          mode={mode}
          modeKnown={planModeKnown(data)}
          modeSwitchable={modeSwitchable}
          settlementStage={sc.settlement_stage ?? null}
          reading={reading}
          busy={busy || extracting}
          act={act}
          onClose={closeEditor}
        />
      )}
    </Section>
  );
}

/** 修改已签署合同的付款批次 = 预算/GM（'agent' 供 e2e 用），与服务端 PUT /payment-plan 的名单一致。
 *  改改 9/21 (fb_9bc4f320) · 从签订版合同读取付款方式 uses the same list (POST /payment-terms/extract). */
const PLAN_AMEND_ROLES = new Set(['gm', 'budget', 'agent']);

/** 改改 9/21 (fb_9cf4aaea) · the plan's mode — the subcontract's, else the schedule summary's; an older server
 *  that sends neither is a recovery plan (that is all it knew). */
function planModeOf(data: ExecutionSummary): PaymentMode {
  return data.subcontract.payment_mode ?? data.payment_schedule?.summary?.payment_mode ?? 'recovery';
}
/** Did the server say which mode the plan is in? Only then is the mode switch offered (an older server has none). */
function planModeKnown(data: ExecutionSummary): boolean {
  return data.subcontract.payment_mode != null || data.payment_schedule?.summary?.payment_mode != null;
}

/** 改改 9/21 (fb_9cf4aaea) · the 结算 stage's default wording, prefilled in the installment editor. */
const SETTLEMENT_LABEL_DEFAULT = '竣工验收合格后支付至结算额';

/** 改改 9/21 (fb_9cf4aaea) · how an installment plan pays — under the plan, in place of PLAN_NETTING_NOTE. */
function installmentNote(settleTo: number | null | undefined): string {
  const to = settleTo != null ? `${fmtPct(settleTo)}%` : 'X%';
  return `首期款签订后支付，不从后续各期扣回；各期按合同额比例支付；结算时付至结算额 ${to}（此处按合同额预估，以结算为准）；质保金质保期满后支付。`
    + ` / The first payment is made on signing and never recovered; each stage pays its share of the contract; the settlement brings the total to ${to} of the settled amount (estimated here on the contract value — the settlement decides); the retention is paid after the DLP.`;
}

/** 改改 9/21 · how 实付 relates to 计量 — under the plan and in its editor. */
const PLAN_NETTING_NOTE = '预付款在各期进度款中按比例扣回，质保金从各期预扣、质保期满后支付；实付合计 = 合同额。'
  + ' / The advance is recovered pro rata from each progress batch and the retention is withheld from each, then paid after the DLP; the payments add up to the contract value.';

const round2 = (n: number) => Math.round(n * 100) / 100;
/** 46.153846… → 「46.15」 — a percentage for display; what is stored keeps full precision. */
const fmtPct = (n: number) => String(round2(n));

/**
 * 改改 9/21 · the share of the contract one point of 计量 actually PAYS. The server recovers the advance pro rata
 * over the progress batches and withholds the retention from each (payments/schedule.ts), so
 * 实付% = 计量% × (100 − 预付% − 质保%) / 100 — and the progress batches sum to 100 − a − r in cash exactly when
 * they sum to 100 gross. A null advance / retention counts as 0. Percentages only — no money is derived here.
 */
function cashFactor(advancePct: number | null | undefined, retentionPct: number | null | undefined): number {
  return round2(100 - (advancePct ?? 0) - (retentionPct ?? 0));
}

/** 说明 — how a row's payable figure comes about, in the server's own figures. */
function payableHow(r: PaymentScheduleRow, mode: PaymentMode = 'recovery'): string {
  // 改改 9/21 (fb_9cf4aaea) · 按节点支付: nothing nets down — a row pays its share of the contract, and the 结算 row
  // is whatever tops the total up to the settle-to % of the 结算额.
  if (mode === 'installment') {
    if (r.kind === 'settlement') return '按结算额计算 / on the settled amount';
    if (r.kind === 'retention') return '质保期满后支付 / after the DLP';
    const pct = r.planned_pct ?? r.payable_pct;
    return pct != null ? `按合同额 ${fmtPct(pct)}% / ${fmtPct(pct)}% of the contract` : '—';
  }
  if (r.kind === 'advance') return '签订后支付 / after signing';
  if (r.kind === 'retention') return '质保期满后支付 / after the DLP';
  if (r.kind !== 'progress' || r.planned_amount_rmb == null) return '—';
  const parts = [`计量 ${r.planned_pct != null ? `${fmtPct(r.planned_pct)}% ` : ''}${rmbCents(r.planned_amount_rmb)}`];
  if (r.advance_recovery_rmb) parts.push(`扣回预付 ${rmbCents(r.advance_recovery_rmb)}`);
  if (r.retention_held_rmb) parts.push(`预扣质保 ${rmbCents(r.retention_held_rmb)}`);
  return parts.join(' − ');
}

/**
 * review R4 (改改 9/21 · fb_9bc4f320) · a stage that already carries a progress cert stays, locked — so the reading's
 * own copy of it must not be added again (华控: 天花 30% certified + 天花 30% read = a phantom ¥65,849.23 stage).
 * Matched by label (ignoring spaces and punctuation), else the earliest remaining one: certs are raised in order.
 */
function readingAfterLocked<T extends { label_zh: string }>(milestones: T[], locked: PaymentScheduleRow[]): { rest: T[]; skipped: T[] } {
  const norm = (t: string | null | undefined) => (t ?? '').replace(/[\s，,。.、:：;；()（）]/g, '').toLowerCase();
  const rest = [...milestones];
  const skipped: T[] = [];
  let unmatched = 0;
  for (const r of locked) {
    const i = rest.findIndex((m) => norm(m.label_zh) === norm(r.label_zh));
    if (i >= 0) skipped.push(...rest.splice(i, 1)); else unmatched++;
  }
  for (let k = 0; k < unmatched && rest.length > 0; k++) skipped.push(rest.shift() as T);
  return { rest, skipped };
}

/**
 * 改改 9/21 (fb_9bc4f320 「能否系统直接识别合同里的付款方式？」) · what 「🤖 从签订版合同读取付款方式」 read off the
 * executed scan, shown ABOVE the editor it prefilled so she checks it against the paper before 保存. The reader
 * never changes advance_pct / retention_pct: where it disagrees with the current terms it says so plainly and
 * she fixes them by hand. Percentages only — no money is derived from the reading.
 */
function ContractReadingSummary({ reading, advancePct, retentionPct, currentMode, modeSwitchable, skipped }: {
  reading: PaymentTermsExtractResult;
  /** review R4 · the reading's stages left out because a certified stage already stands for them. */
  skipped: string[];
  advancePct: number | null;
  retentionPct: number | null;
  currentMode: PaymentMode;
  modeSwitchable: boolean;
}) {
  const p = reading.proposal;
  const installment = p.payment_mode === 'installment';
  const pctOrNone = (n: number | null | undefined) => (n == null ? '无 / none' : `${fmtPct(n)}%`);
  const differs = (a: number | null | undefined, b: number | null | undefined) => Math.abs((a ?? 0) - (b ?? 0)) > 0.005;
  const advDiff = differs(p.advance_pct, advancePct);
  const retDiff = differs(p.retention_pct, retentionPct);
  const advWord = installment ? '首期款' : '预付';
  const parts: string[] = [];
  if (p.advance_pct != null && p.advance_pct > 0) parts.push(`${p.advance_label?.trim() || advWord} ${fmtPct(p.advance_pct)}%`);
  // a recovery reading's pct is the GROSS 计量 share (the advance is recovered from it) — say which it is
  for (const m of p.milestones) parts.push(installment ? `${m.label_zh} ${fmtPct(m.pct)}%` : `${m.label_zh} 计量 ${fmtPct(m.pct)}%`);
  if (p.settlement_stage) parts.push(`结算付至 ${fmtPct(p.settlement_stage.settle_to_pct)}%`);
  if (p.retention_pct != null && p.retention_pct > 0) parts.push(`质保金 ${fmtPct(p.retention_pct)}%`);
  const conditions = p.milestones.filter((m) => m.acceptance_note?.trim());

  return (
    <div className="card" style={{ marginTop: 10, marginBottom: 0, background: 'var(--paper)', wordBreak: 'break-word' }}>
      <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>
        🤖 合同读取结果（尚未保存，请核对后在下方保存）/ read from the signed contract — not saved; check it, then save below
      </div>
      <div className="muted small">来源 / source：{reading.source.file_name}</div>
      <div className="small" style={{ marginTop: 4 }}>
        付款方式 / mode：{installment ? '按节点支付（首期款不抵扣）/ installments' : '预付款按比例抵扣 / advance recovered pro rata'}
      </div>
      <div className="small" style={{ marginTop: 2 }}>{parts.length ? parts.join(' · ') : '（未读到付款比例 / no percentages found）'}</div>
      {conditions.length > 0 && (
        <ul className="muted small" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
          {conditions.map((m, i) => <li key={i}>{m.label_zh}：{m.acceptance_note}</li>)}
        </ul>
      )}
      {p.settlement_stage && <div className="muted small" style={{ marginTop: 2 }}>结算节点 / settlement stage：{p.settlement_stage.label_zh}</div>}
      {p.retention_release_note?.trim() && <div className="muted small" style={{ marginTop: 2 }}>质保金 / retention：{p.retention_release_note}</div>}
      {p.clause_refs.length > 0 && <div className="small" style={{ marginTop: 4 }}>依据条款 / clauses：{p.clause_refs.join('、')}</div>}
      {p.notes.length > 0 && (
        <ul className="small" style={{ margin: '4px 0 0', paddingLeft: 18 }}>
          {p.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
      {skipped.length > 0 && (
        <div className="small" style={{ color: 'var(--warn)', marginTop: 6 }}>
          已申请的节点保留，读取结果中对应的节点已跳过：{skipped.join('、')} / certified stages are kept; their copies in the reading were skipped
        </div>
      )}
      {(advDiff || retDiff) && (
        <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>
          合同读取：{advWord} {pctOrNone(p.advance_pct)}（当前 {pctOrNone(advancePct)}）· 质保 {pctOrNone(p.retention_pct)}（当前 {pctOrNone(retentionPct)}）— 请在上方修改
          {retDiff && '（质保金比例暂无页面修改入口，请联系管理员）'}
          {' / '}the contract reads {advWord === '首期款' ? 'first payment' : 'advance'} {pctOrNone(p.advance_pct)} (now {pctOrNone(advancePct)}) · retention {pctOrNone(p.retention_pct)} (now {pctOrNone(retentionPct)}) — the reader never changes these; amend them above{retDiff ? ' (retention has no editor yet — ask an admin)' : ''}.
        </div>
      )}
      {p.payment_mode !== currentMode && !modeSwitchable && (
        <div className="small" style={{ color: 'var(--warn)', marginTop: 6 }}>
          ⚠ 合同读取的付款方式为「{installment ? '按节点支付' : '预付款按比例抵扣'}」，但已有进度款凭证，付款方式不能切换 — 下方按当前方式预填。
          {' / '}The contract reads as {installment ? 'installments' : 'recovery'}, but progress certs exist, so the mode cannot switch — the editor below keeps the current mode.
        </div>
      )}
      <div className="muted small" style={{ marginTop: 4 }}>模型 / model：{reading.model}</div>
    </div>
  );
}

/**
 * One editable batch row in 「✎ 编辑付款批次」. `id` absent = a brand-new batch.
 * `cash` is what she types — 本期实付占合同额 %; `loadedCash` is what the row showed when the editor opened.
 * `storedPct` is the GROSS pct the row was loaded with, sent back as-is while the cash share still reads what
 * it was loaded as (re-typing the displayed 21.66 must not turn a stored 33.33 into 33.3231) — and ALWAYS for
 * a certified row, which the server freezes to 0.0001 (a round trip through the rounded display would 409).
 * `due_basis` rides along untouched: it is a contractual trigger (结算/验收/…) that this editor has no field
 * for, and defaulting it to 'milestone' on save silently rewrote it on every edit (review 9/20).
 */
type PlanDraftRow = {
  id?: string; label_zh: string; label_en: string;
  cash: string; loadedCash: string; storedPct: number | null;
  due_basis: string | null;
};

/** A stored (GROSS) recovery batch as the editor loads it — its cash share shown, its gross kept. Also a recovery
 *  READING's batch (review R1): its pct is the gross 计量 share too, so untouched it goes back exactly as read. */
function recoveryDraftOf(r: Pick<PaymentScheduleRow, 'milestone_id' | 'label_zh' | 'label_en' | 'planned_pct' | 'due_basis'>, factor: number): PlanDraftRow {
  const loadedCash = r.planned_pct != null && factor > 0 ? String(round2((r.planned_pct * factor) / 100)) : '';
  return {
    id: r.milestone_id ?? undefined,
    label_zh: r.label_zh ?? '',
    label_en: r.label_en ?? '',
    cash: loadedCash, loadedCash,
    storedPct: r.planned_pct,
    due_basis: r.due_basis,
  };
}
/** A batch typed as a CASH share (an AI reading, or an installment stage carried over): converted to gross on save. */
function typedDraftOf(p: { id?: string; label_zh: string; label_en?: string | null; cash: number | null; due_basis?: string | null }): PlanDraftRow {
  return {
    ...(p.id ? { id: p.id } : {}),
    label_zh: p.label_zh, label_en: p.label_en ?? '',
    cash: p.cash != null ? String(p.cash) : '', loadedCash: '', storedPct: null,
    due_basis: p.due_basis ?? null,
  };
}

/**
 * 编辑付款批次 / payment-batch editor (改改 9/20 · fb_3267bf3b). Signed contracts need a reason, exactly
 * like AdvanceTermsEditor: the chopped paper is the contract and this record catches up to it. A batch
 * that already carries a progress cert is rendered LOCKED — the server refuses to move it (409
 * milestone_certified) and a disabled input is a kinder way to say so than a rejection.
 *
 * 改改 9/21: a contract states its stages as CASH shares (预付 30% · 竣工 65% · 质保 5%), so that is what she types.
 * The server still plans each batch as a GROSS share (the advance recovered from it, the retention withheld
 * from it), so a new or edited row is converted — gross = cash × 100 / (100 − a − r), full float precision —
 * and the batches must reach 100 − a − r in cash (±0.01), which is 100 gross.
 *
 * Review 9/21 (R2): the server splits the plan to the exact 分 only when Σ gross is 100 to within 1e-9. Three
 * typed thirds (21.67 × 3 = 65.01, inside the ±0.01) came to Σ 100.0154 — a plan paying ≈ ¥32 MORE than the
 * contract, which is 改改's complaint exactly. So the LAST batch without a cert takes the 尾差: its gross is
 * 100 − Σ(every other gross). Certified batches always send their stored pct; when every batch is certified
 * nothing can move, and a label-only edit is sent with the stored pcts as they are.
 */
function RecoveryPlanEditor({ scId, signed, rows, certified, advancePct, retentionPct, seed, reason, setReason, modeBody, hidden = false, busy, act, onClose }: {
  scId: string;
  signed: boolean;
  rows: PaymentScheduleRow[];
  certified: Set<string>;
  advancePct: number | null;
  retentionPct: number | null;
  /** 改改 9/21 · a draft to start from instead of `rows` — typed cash rows (switching from 按节点支付, or an AI reading). */
  seed?: PlanDraftRow[];
  /** The reason is owned by PaymentPlanEditor, so switching modes keeps what she typed. */
  reason: string;
  setReason: (v: string) => void;
  /** payment_mode 'recovery' + settlement_stage null when the server knows modes; {} for an older server. */
  modeBody: Pick<SetPaymentPlanBody, 'payment_mode' | 'settlement_stage'>;
  /** Kept mounted (and its draft kept) while the other mode is on screen. */
  hidden?: boolean;
  busy: boolean;
  act: (fn: () => Promise<any>, okText?: string) => Promise<void>;
  onClose: () => void;
}) {
  const factor = cashFactor(advancePct, retentionPct);   // = the cash Σ the progress batches must reach
  const [draft, setDraft] = useState<PlanDraftRow[]>(() => seed ?? rows.map((r) => recoveryDraftOf(r, factor)));

  const isLocked = (d: PlanDraftRow) => !!d.id && certified.has(d.id);
  const num = (v: string) => (v.trim() === '' ? NaN : Number(v));
  /** Still reads what it was loaded as — re-typing the same figure (21.66 → 21.660) counts as untouched. */
  const untouched = (d: PlanDraftRow) => d.cash === d.loadedCash || (d.cash.trim() !== '' && num(d.cash) === num(d.loadedCash));
  /** A row keeps its stored gross while untouched; a certified row keeps it, always. */
  const keepsStored = (d: PlanDraftRow) => d.storedPct != null && (isLocked(d) || untouched(d));
  /** The cash share counted toward Σ — exact for a stored row, never its rounded display. */
  const cashOf = (d: PlanDraftRow): number => (keepsStored(d) ? ((d.storedPct as number) * factor) / 100 : num(d.cash));

  const noRoom = factor <= 0;   // 预付% + 质保% ≥ 100: nothing is left for progress batches
  const lastOpen = draft.map(isLocked).lastIndexOf(false);   // the last batch without a cert takes the 尾差
  const allLocked = lastOpen === -1;
  /**
   * The GROSS pct each row sends: stored where kept, else cash × 100 / factor at full float precision — and
   * the last open row 100 − Σ(the rest), so Σ gross is exactly 100 and the server splits the plan to the 分.
   */
  const grosses = (() => {
    const g = draft.map((d) => (keepsStored(d) ? (d.storedPct as number) : (num(d.cash) * 100) / factor));
    if (!allLocked) g[lastOpen] = 100 - g.reduce((t, x, i) => (i === lastOpen ? t : t + x), 0);
    return g;
  })();

  const cashSum = draft.reduce((t, d) => { const c = cashOf(d); return Number.isFinite(c) ? t + c : t; }, 0);
  const balanced = !noRoom && Math.abs(cashSum - factor) <= 0.01 + 1e-9;
  // mirrors the server's own validation (400 milestones_invalid) on what she typed, before the 尾差 moves
  // anything: a label, and a cash share in (0, 100 − a − r] — i.e. a gross pct in (0, 100].
  const rowsValid = draft.length > 0 && draft.every((d) => {
    if (!d.label_zh.trim()) return false;
    if (keepsStored(d)) return (d.storedPct as number) > 0 && (d.storedPct as number) <= 100;
    const c = num(d.cash);
    return !noRoom && Number.isFinite(c) && c > 0 && c <= factor + 1e-9;
  });
  // the 尾差 moves the last open batch by at most 0.01 of cash — only a near-zero last batch can fall out of range
  const tailValid = allLocked || (Number.isFinite(grosses[lastOpen]) && grosses[lastOpen] > 0 && grosses[lastOpen] <= 100);
  const canSave = rowsValid && (allLocked || (balanced && tailValid)) && (!signed || reason.trim().length > 0);
  // what the 尾差 makes the last open batch pay — said out loud when it reads differently from its own input
  const tailCash = !allLocked && balanced && tailValid ? (grosses[lastOpen] * factor) / 100 : null;
  const tailMoves = tailCash != null && fmtPct(tailCash) !== fmtPct(cashOf(draft[lastOpen]));

  function patch(i: number, p: Partial<PlanDraftRow>) { setDraft((d) => d.map((r, idx) => (idx === i ? { ...r, ...p } : r))); }
  // a NEW batch carries no due_basis — the server applies its own default ('milestone').
  function add() { setDraft((d) => [...d, { label_zh: '', label_en: '', cash: '', loadedCash: '', storedPct: null, due_basis: null }]); }
  function remove(i: number) { setDraft((d) => d.filter((_, idx) => idx !== i)); }

  return (
    <div style={hidden ? { display: 'none' } : undefined}>
      <div className="muted small" style={{ marginBottom: 6 }}>
        按签订版合同填写每期进度款的名称与本期实付占合同额的比例。金额由系统按合同价（含税）计算，无需填写。
        {' / '}Enter each progress batch as the signed contract states it — its name and the share of the contract it pays. Amounts are computed server-side off the tax-inclusive contract value.
      </div>
      <div className="small" style={{ fontWeight: 600, marginBottom: 2 }}>
        进度款合计应为 {fmtPct(factor)}%（= 100% − 预付 {fmtPct(advancePct ?? 0)}% − 质保 {fmtPct(retentionPct ?? 0)}%）
        {' / '}the progress batches must total {fmtPct(factor)}%
      </div>
      <div className="muted small" style={{ marginBottom: 8 }}>{PLAN_NETTING_NOTE}</div>
      {noRoom && (
        <div className="small" style={{ color: 'var(--bad)', marginBottom: 8 }}>
          预付 + 质保已达 100% 或以上，没有可分配给进度款的比例 — 请先核对预付款/质保金比例。/ advance + retention reach 100% — nothing is left for progress batches; check those percentages first.
        </div>
      )}

      {draft.map((d, i) => {
        const locked = isLocked(d);
        return (
          <div key={d.id ?? `new-${i}`} className="row-wrap" style={{ gap: 8, alignItems: 'flex-end', marginBottom: 6 }}>
            <div className="field grow" style={{ marginBottom: 0, minWidth: 160 }}>
              <label>名称（中文）/ label <span style={{ color: 'var(--bad)' }}>*</span></label>
              <input value={d.label_zh} disabled={busy} placeholder="例如：主体完工 / e.g. structure complete"
                onChange={(e) => patch(i, { label_zh: e.target.value })} />
            </div>
            <div className="field grow" style={{ marginBottom: 0, minWidth: 140 }}>
              <label>English（选填 / optional）</label>
              <input value={d.label_en} disabled={busy} placeholder="Structure complete"
                onChange={(e) => patch(i, { label_en: e.target.value })} />
            </div>
            <div className="field" style={{ width: 170, marginBottom: 0 }}>
              <label>本期实付占合同额 % <span style={{ color: 'var(--bad)' }}>*</span></label>
              <input type="number" min={0} max={100} step="0.01" value={d.cash} disabled={busy || locked}
                title={locked ? '已申请进度款，比例已冻结 / a progress cert exists — this % is frozen' : '本期实际支付的金额占合同额的比例 / the share of the contract this batch actually pays'}
                onChange={(e) => patch(i, { cash: e.target.value })} />
            </div>
            {locked
              ? <span className="pill pill-muted small" title="已申请进度款，比例已冻结（名称仍可更正）/ a progress cert exists — the % is frozen (the label can still be corrected)">🔒 已申请进度款 / certified</span>
              : <button className="small" disabled={busy} onClick={() => remove(i)} title="删除该期 / remove this batch">✕ 删除</button>}
          </div>
        );
      })}

      <div className="row-wrap" style={{ gap: 8, alignItems: 'center', marginTop: 4 }}>
        <button className="small" disabled={busy} onClick={add}>＋ 增加一期 / Add batch</button>
        {allLocked
          ? <span className="small muted">各期均已申请进度款，比例不可修改（名称仍可更正）/ every batch is certified — only the labels can change</span>
          : !noRoom && (
            <span className="small" style={{ color: balanced ? 'var(--good)' : 'var(--bad)', fontWeight: 600 }}>
              Σ 实付 {fmtPct(cashSum)}%{balanced ? ' ✓' : ` — 须为 ${fmtPct(factor)}%（±0.01）/ must total ${fmtPct(factor)}%`}
            </span>
          )}
      </div>
      {tailMoves && (
        <div className="small muted" style={{ marginTop: 4 }}>
          尾差计入最后一期：「{draft[lastOpen].label_zh.trim() || '—'}」实付按 {fmtPct(tailCash as number)}% 保存，使合计恰为 {fmtPct(factor)}%。
          {' / '}The rounding goes to the last batch: it is saved at {fmtPct(tailCash as number)}% so the batches total exactly {fmtPct(factor)}%.
        </div>
      )}

      {signed && (
        <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
          <label>修改原因（必填，合同已签署）/ reason (required — signed)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy}
            placeholder="例如：合同第4.2条约定分三期支付 / e.g. clause 4.2 sets three instalments" />
        </div>
      )}

      <div className="row-wrap" style={{ gap: 8, marginTop: 8 }}>
        <button className="btn-primary small" disabled={busy || !canSave}
          onClick={() => act(async () => {
            const milestones: PaymentPlanMilestoneInput[] = draft.map((d, i) => ({
              ...(d.id ? { id: d.id } : {}),
              label_zh: d.label_zh.trim(),
              ...(d.label_en.trim() ? { label_en: d.label_en.trim() } : {}),
              pct: grosses[i],
              ...(d.due_basis ? { due_basis: d.due_basis } : {}),
            }));
            await execActions.setPaymentPlan(scId, { milestones, ...modeBody, ...(signed && reason.trim() ? { reason: reason.trim() } : {}) });
            onClose();
          }, `付款计划已更新 · 共 ${draft.length} 期 / payment plan saved — ${draft.length} batches`)}>
          保存 / Save
        </button>
        <button className="small" disabled={busy} onClick={onClose}>取消 / Cancel</button>
        {!rowsValid && (!noRoom || allLocked) && (
          <span className="small" style={{ color: 'var(--bad)' }}>
            每期需填名称，实付比例须大于 0 且不超过 {fmtPct(factor)}% / each batch needs a name and a cash % in (0, {fmtPct(factor)}]
          </span>
        )}
        {rowsValid && balanced && !tailValid && (
          <span className="small" style={{ color: 'var(--bad)' }}>
            最后一期实付过小，无法吸收尾差 — 请调整各期比例 / the last batch is too small to take the rounding — adjust the batches
          </span>
        )}
      </div>
    </div>
  );
}

/** One stage of an installment plan in the editor. `pct` is what she types — the share of the contract paid at
 *  that stage, sent AS IS; `storedPct` is what a certified stage was loaded with (sent back untouched). */
type StageDraftRow = { id?: string; label_zh: string; label_en: string; pct: string; storedPct: number | null; due_basis: string | null };
/** The 结算 stage in the editor: its label and 「支付至结算额 %」. */
type SettlementDraft = { label_zh: string; label_en: string; settleTo: string };

/**
 * 编辑付款批次 — 改改 9/21 (fb_9cf4aaea 「质保金前的这笔付款，是支付至项目结算额的95%」). Her contract pays 首期款 30% on
 * signing, 天花隐蔽 30%, then 「工程竣工验收合格后10个工作日支付至结算额95%」, then 质保金 5%. That third stage is not a
 * share of the contract at all, and the recovery editor — whose batches must reach 100 − 预付 − 质保 — forced it
 * to one, so she typed 0. 「付款方式」 picks the model; each keeps its own draft while the other is on screen, and
 * the reason is shared. The mode can switch only while no 进度款凭证 exists (the server 409s otherwise).
 *
 * fb_9bc4f320: with an AI `reading`, both drafts start from it (certified stages kept, locked) and the reason is
 * prefilled with the clauses it cites. Nothing is saved until 保存.
 */
function PaymentPlanEditor({ scId, signed, rows, certified, advancePct, retentionPct, mode, modeKnown, modeSwitchable, settlementStage, reading, busy, act, onClose }: {
  scId: string;
  signed: boolean;
  rows: PaymentScheduleRow[];
  certified: Set<string>;
  advancePct: number | null;
  retentionPct: number | null;
  /** The plan's current mode; `modeKnown` = the server said so (an older server offers no switch). */
  mode: PaymentMode;
  modeKnown: boolean;
  modeSwitchable: boolean;
  settlementStage: SettlementStage | null;
  reading: PaymentTermsExtractResult | null;
  busy: boolean;
  act: (fn: () => Promise<any>, okText?: string) => Promise<void>;
  onClose: () => void;
}) {
  const factor = cashFactor(advancePct, retentionPct);
  const proposal = reading?.proposal ?? null;
  const lockedRows = rows.filter((r) => !!r.milestone_id && certified.has(r.milestone_id));
  // review R4: the reading's stages minus the ones a certified (locked) stage already stands for
  const readingRest = proposal ? readingAfterLocked(proposal.milestones, lockedRows).rest : [];
  const readRecovery = proposal?.payment_mode === 'recovery';
  const [editMode, setEditMode] = useState<PaymentMode>(() => (proposal && (modeSwitchable || proposal.payment_mode === mode) ? proposal.payment_mode : mode));
  const [reason, setReason] = useState(() => (proposal
    ? `按签订版合同付款条款${proposal.clause_refs.length ? `（${proposal.clause_refs.join('、')}）` : ''}`
    : ''));

  // ── recovery draft: undefined = RecoveryPlanEditor loads `rows` exactly as it always has ──
  const [recoverySeed] = useState<PlanDraftRow[] | undefined>(() => {
    if (proposal) {
      return [
        ...lockedRows.map((r) => recoveryDraftOf(r, factor)),
        // review R1: a recovery reading states GROSS 计量 shares (Σ 100) — seeded as stored gross, so untouched
        // rows go back exactly; an installment reading states cash shares, typed as such.
        ...readingRest.map((m) => (readRecovery
          ? recoveryDraftOf({ milestone_id: null, label_zh: m.label_zh, label_en: m.label_en, planned_pct: m.pct, due_basis: null }, factor)
          : typedDraftOf({ label_zh: m.label_zh, label_en: m.label_en, cash: m.pct }))),
      ];
    }
    // coming from 按节点支付: each stage's pct already IS its cash share — carried over as typed
    if (mode === 'installment') {
      return rows.map((r) => typedDraftOf({ id: r.milestone_id ?? undefined, label_zh: r.label_zh ?? '', label_en: r.label_en, cash: r.planned_pct, due_basis: r.due_basis }));
    }
    return undefined;
  });

  // ── installment draft ──
  // Coming from recovery, the LAST batch is the completion payment — under 按节点支付 that is the 结算 stage
  // itself, so it is not carried over as a flat stage (and the editor says so).
  const [carriedOver] = useState(() => {
    if (proposal) return readRecovery && readingRest.length > 0 ? readingRest[readingRest.length - 1].label_zh : null;
    return mode === 'recovery' && rows.length > 0 ? (rows[rows.length - 1].label_zh ?? '') : null;
  });
  const [stageSeed] = useState<StageDraftRow[]>(() => {
    const stored = (r: PaymentScheduleRow): StageDraftRow => ({
      id: r.milestone_id ?? undefined, label_zh: r.label_zh ?? '', label_en: r.label_en ?? '',
      pct: r.planned_pct != null ? String(r.planned_pct) : '', storedPct: r.planned_pct, due_basis: r.due_basis,
    });
    if (proposal) {
      // review R1: a recovery reading is gross — converted to cash like the no-reading path, and its last batch
      // (the completion payment) becomes the 结算 stage rather than a flat stage
      const stages = readRecovery ? readingRest.slice(0, -1) : readingRest;
      return [
        ...(mode === 'installment' ? lockedRows.map(stored) : []),
        ...stages.map((m) => ({
          label_zh: m.label_zh, label_en: m.label_en ?? '',
          pct: readRecovery ? (factor > 0 ? String(round2((m.pct * factor) / 100)) : '') : String(m.pct),
          storedPct: null, due_basis: null,
        })),
      ];
    }
    if (mode === 'installment') return rows.map(stored);
    return rows.slice(0, -1).map((r) => ({
      id: r.milestone_id ?? undefined, label_zh: r.label_zh ?? '', label_en: r.label_en ?? '',
      pct: r.planned_pct != null && factor > 0 ? String(round2((r.planned_pct * factor) / 100)) : '',
      storedPct: null, due_basis: r.due_basis,
    }));
  });
  const [settle, setSettle] = useState<SettlementDraft>(() => {
    const st = proposal?.settlement_stage ?? settlementStage;
    if (st) return { label_zh: st.label_zh, label_en: st.label_en ?? '', settleTo: String(st.settle_to_pct) };
    return { label_zh: SETTLEMENT_LABEL_DEFAULT, label_en: '', settleTo: String(round2(100 - (retentionPct ?? 0))) };
  });

  const settleToShown = settle.settleTo.trim() !== '' && Number.isFinite(Number(settle.settleTo)) ? fmtPct(Number(settle.settleTo)) : 'X';
  const modeName = (m: PaymentMode) => (m === 'installment' ? '按节点支付 / installments' : '预付款按比例抵扣 / advance recovered pro rata');

  return (
    <div className="card" style={{ marginTop: 10, borderColor: 'var(--accent)' }}>
      <div className="small" style={{ fontWeight: 600, marginBottom: 6 }}>编辑付款批次 / Edit payment batches</div>

      {modeKnown && (
        <div style={{ marginBottom: 8 }}>
          <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>付款方式 / Payment mode</div>
          {modeSwitchable ? (
            <div className="row-wrap small" style={{ gap: '6px 16px' }}>
              <label className="row" style={{ gap: 6, cursor: 'pointer', alignItems: 'flex-start' }}>
                <input type="radio" name={`plan-mode-${scId}`} checked={editMode === 'installment'} disabled={busy} onChange={() => setEditMode('installment')} />
                <span>按节点支付（首期款不抵扣，竣工付至结算额 {settleToShown}%）/ installments</span>
              </label>
              <label className="row" style={{ gap: 6, cursor: 'pointer', alignItems: 'flex-start' }}>
                <input type="radio" name={`plan-mode-${scId}`} checked={editMode === 'recovery'} disabled={busy} onChange={() => setEditMode('recovery')} />
                <span>预付款按比例抵扣 / advance recovered pro rata</span>
              </label>
            </div>
          ) : (
            <div className="muted small">
              {modeName(mode)} — 已有进度款凭证，付款方式不能切换 / progress certs exist, so the mode cannot switch
            </div>
          )}
          {editMode !== mode && (
            <div className="small" style={{ color: 'var(--warn)', marginTop: 4 }}>
              保存后付款方式将由「{modeName(mode)}」改为「{modeName(editMode)}」/ saving switches the payment mode
            </div>
          )}
        </div>
      )}

      <RecoveryPlanEditor
        hidden={editMode !== 'recovery'}
        scId={scId} signed={signed} rows={rows} certified={certified}
        advancePct={advancePct} retentionPct={retentionPct}
        seed={recoverySeed}
        reason={reason} setReason={setReason}
        modeBody={modeKnown ? { payment_mode: 'recovery', settlement_stage: null } : {}}
        busy={busy} act={act} onClose={onClose}
      />
      {modeKnown && (
        <InstallmentPlanEditor
          hidden={editMode !== 'installment'}
          scId={scId} signed={signed} certified={certified}
          advancePct={advancePct} retentionPct={retentionPct}
          seed={stageSeed} carriedOver={carriedOver}
          settle={settle} setSettle={setSettle}
          reason={reason} setReason={setReason}
          busy={busy} act={act} onClose={onClose}
        />
      )}
    </div>
  );
}

/**
 * 按节点支付 / installment editor — 改改 9/21 (fb_9cf4aaea). Each stage pays 「本期支付占合同额 %」 exactly as typed —
 * no cash→gross conversion and no 尾差 on the last row: nothing is recovered from it and nothing is withheld, so
 * what she types IS what the stage pays. The 结算 stage is separate: it brings the total up to 「支付至结算额 %」
 * of the settled amount, so its share is only an estimate here. The one arithmetic rule (the server's 400
 * installments_exceed_settlement): 首期款% + Σ stages ≤ 付至%. The hint is percentages only — no money.
 */
function InstallmentPlanEditor({ scId, signed, certified, advancePct, retentionPct, seed, carriedOver, settle, setSettle, reason, setReason, hidden = false, busy, act, onClose }: {
  scId: string;
  signed: boolean;
  certified: Set<string>;
  advancePct: number | null;
  retentionPct: number | null;
  seed: StageDraftRow[];
  /** The recovery plan's last batch, not carried over because it becomes the 结算 stage (null = nothing dropped). */
  carriedOver: string | null;
  settle: SettlementDraft;
  setSettle: (s: SettlementDraft) => void;
  reason: string;
  setReason: (v: string) => void;
  hidden?: boolean;
  busy: boolean;
  act: (fn: () => Promise<any>, okText?: string) => Promise<void>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<StageDraftRow[]>(seed);
  const num = (v: string) => (v.trim() === '' ? NaN : Number(v));
  const isLocked = (d: StageDraftRow) => !!d.id && certified.has(d.id) && d.storedPct != null;
  /** A certified stage sends what it was stored with — the server freezes it. */
  const pctOf = (d: StageDraftRow): number => (isLocked(d) ? (d.storedPct as number) : num(d.pct));

  const a = advancePct ?? 0;
  const r = retentionPct ?? 0;
  const stageSum = draft.reduce((t, d) => { const v = pctOf(d); return Number.isFinite(v) ? t + v : t; }, 0);
  const paidBefore = a + stageSum;
  const settleTo = num(settle.settleTo);
  // mirrors the server's own checks (400 milestones_invalid / settlement_stage_invalid) before it is asked
  const rowsValid = draft.every((d) => {
    if (!d.label_zh.trim()) return false;
    const v = pctOf(d);
    return Number.isFinite(v) && v > 0 && v <= 100;
  });
  const settleValid = settle.label_zh.trim() !== '' && Number.isFinite(settleTo) && settleTo > 0 && settleTo <= 100;
  const exceeds = settleValid && paidBefore > settleTo + 1e-9;
  const estimate = settleValid && !exceeds ? settleTo - paidBefore : null;
  // 付至% + 质保% is 100 on a normal contract (95 + 5) — said, never enforced: the paper governs.
  const topsOff = settleValid && Math.abs(settleTo + r - 100) > 0.005;
  const canSave = rowsValid && settleValid && !exceeds && (!signed || reason.trim().length > 0);

  // 「首期款 30% + 各期 30% = 60%，结算时付至 95%（预估 35%），质保金 5%」
  const hint = (() => {
    const bits: string[] = [];
    if (a > 0 && draft.length > 0) bits.push(`首期款 ${fmtPct(a)}% + 各期 ${fmtPct(stageSum)}% = ${fmtPct(paidBefore)}%`);
    else if (a > 0) bits.push(`首期款 ${fmtPct(a)}%`);
    else if (draft.length > 0) bits.push(`各期 ${fmtPct(stageSum)}%`);
    bits.push(`结算时付至 ${settleValid ? fmtPct(settleTo) : '—'}%${estimate != null ? `（预估 ${fmtPct(estimate)}%）` : ''}`);
    if (r > 0) bits.push(`质保金 ${fmtPct(r)}%`);
    return bits.join('，');
  })();

  function patch(i: number, p: Partial<StageDraftRow>) { setDraft((d) => d.map((x, idx) => (idx === i ? { ...x, ...p } : x))); }
  function add() { setDraft((d) => [...d, { label_zh: '', label_en: '', pct: '', storedPct: null, due_basis: null }]); }
  function remove(i: number) { setDraft((d) => d.filter((_, idx) => idx !== i)); }

  async function save() {
    const milestones: PaymentPlanMilestoneInput[] = draft.map((d) => ({
      ...(d.id ? { id: d.id } : {}),
      label_zh: d.label_zh.trim(),
      ...(d.label_en.trim() ? { label_en: d.label_en.trim() } : {}),
      pct: pctOf(d),
      ...(d.due_basis ? { due_basis: d.due_basis } : {}),
    }));
    const body: SetPaymentPlanBody = {
      milestones,
      payment_mode: 'installment',
      settlement_stage: { label_zh: settle.label_zh.trim(), label_en: settle.label_en.trim() || null, settle_to_pct: settleTo },
      ...(signed && reason.trim() ? { reason: reason.trim() } : {}),
    };
    await execActions.setPaymentPlan(scId, body);
    onClose();
  }

  return (
    <div style={hidden ? { display: 'none' } : undefined}>
      <div className="muted small" style={{ marginBottom: 6 }}>
        按签订版合同逐期填写：首期款签订后支付、不抵扣（比例即上方「分包合同」的首期款 {fmtPct(a)}%）；各期填本期支付占合同额的比例，原样保存；
        竣工后的「支付至结算额 %」填在下方结算节点。金额由系统按合同价（含税）计算。
        {' / '}Enter the stages as the signed contract states them: the first payment ({fmtPct(a)}%, set in the Subcontract block above) is paid on signing and never recovered; each stage pays its share of the contract, saved as typed; the settle-to % goes in the settlement stage below. Amounts are computed server-side.
      </div>
      {carriedOver != null && (
        <div className="small muted" style={{ marginBottom: 6 }}>
          原最后一期「{carriedOver || '—'}」未带入 — 按节点支付时它就是下方的结算节点；如不是，请「＋ 增加一期」补回。
          {' / '}The former last batch is not carried over — under installments it is the settlement stage below; add it back if it is not.
        </div>
      )}

      {draft.length === 0 && <div className="muted small" style={{ marginBottom: 6 }}>无中间节点（首期款后直接结算）/ no stages between the first payment and the settlement</div>}
      {draft.map((d, i) => {
        const locked = isLocked(d);
        return (
          <div key={d.id ?? `new-${i}`} className="row-wrap" style={{ gap: 8, alignItems: 'flex-end', marginBottom: 6 }}>
            <div className="field grow" style={{ marginBottom: 0, minWidth: 160 }}>
              <label>节点名称（中文）/ stage <span style={{ color: 'var(--bad)' }}>*</span></label>
              <input value={d.label_zh} disabled={busy} placeholder="例如：天花隐蔽验收合格后10个工作日 / e.g. ceiling concealed-works accepted"
                onChange={(e) => patch(i, { label_zh: e.target.value })} />
            </div>
            <div className="field grow" style={{ marginBottom: 0, minWidth: 140 }}>
              <label>English（选填 / optional）</label>
              <input value={d.label_en} disabled={busy} onChange={(e) => patch(i, { label_en: e.target.value })} />
            </div>
            <div className="field" style={{ width: 170, marginBottom: 0 }}>
              <label>本期支付占合同额 % <span style={{ color: 'var(--bad)' }}>*</span></label>
              <input type="number" min={0} max={100} step="0.01" inputMode="decimal" value={d.pct} disabled={busy || locked}
                title={locked ? '已申请进度款，比例已冻结 / a progress cert exists — this % is frozen' : '本期支付金额占合同额的比例，原样保存 / the share of the contract this stage pays, saved as typed'}
                onChange={(e) => patch(i, { pct: e.target.value })} />
            </div>
            {locked
              ? <span className="pill pill-muted small" title="已申请进度款，比例已冻结（名称仍可更正）/ a progress cert exists — the % is frozen">🔒 已申请进度款 / certified</span>
              : <button className="small" disabled={busy} onClick={() => remove(i)} title="删除该节点 / remove this stage">✕ 删除</button>}
          </div>
        );
      })}
      <button className="small" disabled={busy} onClick={add}>＋ 增加一期 / Add stage</button>

      {/* the 结算 stage — 「工程竣工验收合格后10个工作日支付至结算额95%」 */}
      <div className="card" style={{ marginTop: 10, marginBottom: 0, background: 'var(--paper)' }}>
        <div className="small" style={{ fontWeight: 600, marginBottom: 4 }}>结算节点 / Settlement stage</div>
        <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
          <div className="field grow" style={{ marginBottom: 0, minWidth: 180 }}>
            <label>名称（中文）/ label <span style={{ color: 'var(--bad)' }}>*</span></label>
            <input value={settle.label_zh} disabled={busy} onChange={(e) => setSettle({ ...settle, label_zh: e.target.value })} />
          </div>
          <div className="field grow" style={{ marginBottom: 0, minWidth: 140 }}>
            <label>English（选填 / optional）</label>
            <input value={settle.label_en} disabled={busy} onChange={(e) => setSettle({ ...settle, label_en: e.target.value })} />
          </div>
          <div className="field" style={{ width: 150, marginBottom: 0 }}>
            <label>支付至结算额 % <span style={{ color: 'var(--bad)' }}>*</span></label>
            <input type="number" min={0} max={100} step="0.01" inputMode="decimal" value={settle.settleTo} disabled={busy}
              onChange={(e) => setSettle({ ...settle, settleTo: e.target.value })} />
          </div>
        </div>
        <div className="muted small" style={{ marginTop: 4 }}>
          结算节点的金额 = 结算额 × 付至% − 已付各期，此处按合同额预估，以结算为准。/ the settlement stage pays settled amount × settle-to % minus what was paid — estimated here on the contract value.
        </div>
      </div>

      <div className="small" style={{ marginTop: 8, fontWeight: 600, color: exceeds ? 'var(--bad)' : 'var(--ink)', wordBreak: 'break-word' }}>
        {hint}
      </div>
      {exceeds && (
        <div className="small" style={{ color: 'var(--bad)' }}>
          首期款与各期合计 {fmtPct(paidBefore)}% 已超过结算付至 {fmtPct(settleTo)}% — 请调整 / the first payment plus the stages ({fmtPct(paidBefore)}%) exceed the settle-to {fmtPct(settleTo)}%
        </div>
      )}
      {topsOff && !exceeds && (
        <div className="small" style={{ color: 'var(--warn)' }}>
          结算付至 {fmtPct(settleTo)}% + 质保金 {fmtPct(r)}% = {fmtPct(settleTo + r)}%，不是 100% — 请与签订版合同核对 / settle-to + retention is not 100% — check against the paper
        </div>
      )}

      {signed && (
        <div className="field" style={{ marginTop: 8, marginBottom: 0 }}>
          <label>修改原因（必填，合同已签署）/ reason (required — signed)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} disabled={busy}
            placeholder="例如：合同第4.2条约定按节点支付 / e.g. clause 4.2 sets the stages" />
        </div>
      )}

      <div className="row-wrap" style={{ gap: 8, marginTop: 8 }}>
        <button className="btn-primary small" disabled={busy || !canSave}
          onClick={() => act(save, `付款计划已保存 · 按节点支付 ${draft.length} 期 + 结算 / payment plan saved — ${draft.length} stage(s) + settlement`)}>
          保存 / Save
        </button>
        <button className="small" disabled={busy} onClick={onClose}>取消 / Cancel</button>
        {!rowsValid && (
          <span className="small" style={{ color: 'var(--bad)' }}>
            每期需填名称，比例须大于 0 且不超过 100 / each stage needs a name and a % in (0, 100]
          </span>
        )}
        {!settleValid && (
          <span className="small" style={{ color: 'var(--bad)' }}>
            结算节点需填名称，「支付至结算额 %」须大于 0 且不超过 100 / the settlement stage needs a label and a % in (0, 100]
          </span>
        )}
      </div>
    </div>
  );
}

/** 补录税率 = 预算/GM（'agent' 供 e2e 用），与服务端 POST /tax-rate 的名单一致。 */
const TAX_RATE_ROLES = new Set(['gm', 'budget', 'agent']);

/**
 * 补录税率 / enter a missing tax rate — 改改 9/21. A contract locked without a 税金点 cannot form a single 含税
 * figure, so its advance, plan, certs and settlement all refuse (409 tax_pct_missing) — and once signed the old
 * fix (撤销锁定 → 补税率 → 重新锁定) is refused too (409 subcontract_signed). This fills the rate the signed
 * paper states, once, with a required reason the server audits. Like RevertSignature it owns only its own
 * state; the parent refreshes its reads through onDone.
 */
function TaxRateEditor({ scId, role, disabled, onDone }: {
  scId: string;
  /** me.account.role — renders for the roles the route accepts (the server re-checks). */
  role: string | null | undefined;
  disabled?: boolean;
  /** Parent-owned message + cache invalidation, awaited before the editor closes. */
  onDone: (taxPct: number) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // hooks first, then the role gate — never return before the useState calls.
  if (!TAX_RATE_ROLES.has(role ?? '')) return null;

  const n = pct.trim() === '' ? NaN : Number(pct);
  // mirrors the 税金点 check on the 议价 lock (0–100); the server re-checks (400 tax_pct_invalid)
  const valid = Number.isFinite(n) && n >= 0 && n <= 100;

  function close() { setOpen(false); setPct(''); setReason(''); setErr(null); }

  async function save() {
    const r = reason.trim();
    if (!valid || !r) return;
    setBusy(true); setErr(null);
    try {
      const result = await execActions.setTaxRate(scId, { tax_pct: n, reason: r });
      const saved = result.subcontract?.final_tax_pct;
      await onDone(typeof saved === 'number' ? saved : n);
      close();
    } catch (e) {
      // the 400/409 `detail` says what is wrong (already set / not signed / out of range)
      setErr(errText(e));
    } finally { setBusy(false); }
  }

  if (!open) {
    return (
      <div className="row-wrap" style={{ gap: 8, alignItems: 'center', marginTop: 6 }}>
        <span className="small" style={{ color: 'var(--warn)' }}>
          ⚠ 本合同未录入税率，含税金额、预付款与付款均无法办理 / no tax rate on this contract — tax-inclusive amounts and payments are blocked
        </span>
        <button className="small" disabled={disabled} onClick={() => { setOpen(true); setErr(null); }}
          title="按签订版合同补录税率（只能补录一次，需填写原因）/ enter the rate the signed contract states — once, with a reason">
          ＋ 补录税率 / Enter tax rate
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end' }}>
        <div className="field" style={{ width: 130, marginBottom: 0 }}>
          <label>税率 % / tax rate %</label>
          <input type="number" min={0} max={100} step="0.01" inputMode="decimal" value={pct} disabled={busy}
            onChange={(e) => setPct(e.target.value)} placeholder="如 9 / e.g. 9" />
        </div>
        <div className="field grow" style={{ marginBottom: 0, minWidth: 220 }}>
          <label>补录原因（必填）/ reason (required)</label>
          <input value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)}
            placeholder="例如：签订版合同第4.1条约定税率 9% / e.g. clause 4.1 of the signed contract sets 9%" />
        </div>
        <button className="btn-primary small" disabled={busy || !!disabled || !valid || !reason.trim()} onClick={() => void save()}>
          {busy ? '保存中… / saving…' : '保存 / Save'}
        </button>
        <button className="small" disabled={busy} onClick={close}>取消 / Cancel</button>
        {pct.trim() !== '' && !valid && <span className="small" style={{ color: 'var(--bad)' }}>0–100</span>}
      </div>
      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{err}</div>}
    </div>
  );
}

/**
 * 预付款条款 / advance terms editor (改改 9/20, fb_f81de749). Draft: free edit. Signed: reason required — the
 * chopped paper is the contract; this record catches up to it, and the reason lands in the audit trail.
 * The parent hides it once any payment cert exists (the server refuses too: certs snapshot their §14·0
 * recovery from the schedule, so the advance can't move under them).
 */
function AdvanceTermsEditor({ scId, signed, currentPct, installment = false, ceiling = null, busy, act }: {
  scId: string; signed: boolean; currentPct: number | null;
  /** 改改 9/21 (fb_9cf4aaea) · 按节点支付 calls the same advance_pct 「首期款」 — same field, her contract's word. */
  installment?: boolean;
  /** 按节点支付: settle-to % − Σ stages — the most the 首期款 may be (the server's installments_exceed_settlement). */
  ceiling?: number | null;
  busy: boolean;
  act: (fn: () => Promise<any>, okText?: string) => Promise<void>;
}) {
  const word = installment ? '首期款' : '预付款';
  const wordEn = installment ? 'first payment' : 'advance';
  const [open, setOpen] = useState(false);
  const [pct, setPct] = useState(currentPct != null ? String(currentPct) : '');
  const [reason, setReason] = useState('');
  const n = pct.trim() === '' ? null : Number(pct);
  const valid = n == null || (Number.isFinite(n) && n > 0 && n <= 100);
  const overCeiling = ceiling != null && n != null && Number.isFinite(n) && n > ceiling + 1e-9;
  const unchanged = (n ?? null) === (currentPct ?? null);
  if (!open) {
    return (
      <div style={{ marginTop: 6 }}>
        <button className="small" disabled={busy} onClick={() => setOpen(true)}
          title={currentPct == null ? `合同有${word}？在这里设置比例，之后才能申请${word} / set the ${wordEn} % here to enable its cert` : `修改${word}比例 / amend the ${wordEn} %`}>
          {currentPct == null ? `＋ 设置${word}比例 / Set ${wordEn} %` : `✎ 修改${word}比例 / Amend ${wordEn} %`}
        </button>
      </div>
    );
  }
  return (
    <div className="row-wrap" style={{ gap: 8, alignItems: 'flex-end', marginTop: 8 }}>
      <div className="field" style={{ width: 150, marginBottom: 0 }}>
        <label>{word}比例 % / {wordEn} %</label>
        <input type="number" min={0} max={100} value={pct} onChange={(e) => setPct(e.target.value)} placeholder="留空=无预付 / blank=none" disabled={busy} />
      </div>
      {signed && (
        <div className="field grow" style={{ marginBottom: 0, minWidth: 220 }}>
          <label>修改原因（必填，合同已签署）/ reason (required — signed)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="例如：合同第14条约定预付10% / e.g. clause 14 sets a 10% advance" disabled={busy} />
        </div>
      )}
      <button className="btn-primary small" disabled={busy || !valid || overCeiling || unchanged || (signed && !reason.trim())}
        onClick={() => act(async () => {
          await execActions.setAdvanceTerms(scId, { advance_pct: n, reason: signed ? reason.trim() : undefined });
          setOpen(false); setReason('');
        }, n == null ? `已清除${word} / ${wordEn} cleared` : `${word}比例已设为 ${n}% / ${wordEn} set to ${n}%`)}>
        保存 / Save
      </button>
      <button className="small" disabled={busy} onClick={() => { setOpen(false); setPct(currentPct != null ? String(currentPct) : ''); setReason(''); }}>取消 / Cancel</button>
      {!valid && <span className="small" style={{ color: 'var(--bad)' }}>0–100</span>}
      {overCeiling && (
        <span className="small" style={{ color: 'var(--bad)' }}>
          首期款 + 各期不能超过结算付至比例 — 首期款最多 {fmtPct(ceiling as number)}% / the first payment plus the stages cannot pass the settle-to % — at most {fmtPct(ceiling as number)}%
        </span>
      )}
    </div>
  );
}

/** st.13 — generate the subcontract from the awarded bid (the bridge into execution). 改改 9/22 「公司劳务合同都不扣回。
 *  以后新合同默认这样，不用每次改」 · Eric 9/23: a BLANK 首期款 is left out of the request so the server applies the
 *  company plan (settings.contract, mirroring 合同模板 4.2.2–4.2.6 — 分期付款, 无预付款, 付至 50% → 80% → 结算总额
 *  95%, 质保金 5%); typing a number sets one anyway. Retention defaults 5%. Whatever is typed is what's used. */
function GenerateSubcontractForm({ pid, awardedBidTotal, awardedTaxPct }: { pid: string; awardedBidTotal: number | null; awardedTaxPct: number | null }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(awardedBidTotal != null ? String(awardedBidTotal) : '');
  const [advance, setAdvance] = useState('');
  const [retention, setRetention] = useState('5');
  const [dlp, setDlp] = useState('24');
  const [contractId, setContractId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function generate() {
    setBusy(true); setErr(null);
    try {
      await execActions.generateSubcontract(pid, {
        value_rmb: value ? Number(value) : null,
        // blank → omitted → the server's company default (改改 9/22 · Eric 9/23: 无预付款, per 4.2.2); '0' → none too
        advance_pct: advance.trim() !== '' ? Number(advance) : undefined,
        retention_pct: retention ? Number(retention) : null,
        dlp_months: dlp ? Number(dlp) : null,
        contract_id: contractId || undefined,
      });
      await qc.invalidateQueries({ queryKey: ['subcontracts', pid] });
    // review 9/23: show the server's bilingual detail — a bare `installments_exceed_settlement` means nothing to 改改
    } catch (e: any) { setErr(e?.body?.detail ?? e?.body?.error ?? e?.message ?? '生成失败 / failed'); }
    finally { setBusy(false); }
  }

  return (
    <div className="card">
      <strong>生成分包合同 / Generate subcontract (st.13)</strong>
      <p className="small muted" style={{ margin: '4px 0 10px' }}>从已定标的报价生成分包合同与付款计划，进入执行阶段。/ Creates the subcontract + payment schedule from the awarded bid, opening the execution stage.</p>
      <div className="row-wrap" style={{ alignItems: 'flex-end' }}>
        {/* 改改 9/20 · the stored 合同额 stays the LOCKED ex-tax price (that is what 锁价 wrote and what the
            ledger reconciles against); the 含税 twin is shown beside it so nobody types one for the other. */}
        <div className="field" style={{ width: 200, marginBottom: 0 }}>
          <label>合同额（不含税，锁定价）/ value (pre-tax, locked) (¥)</label>
          <input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="默认=中标价" />
          {awardedTaxPct != null && Number(value) > 0 && (
            <div className="small muted" style={{ wordBreak: 'break-word' }}>
              含税 {rmb(Math.round(Number(value) * (100 + awardedTaxPct)) / 100)} · 税率 {awardedTaxPct}%
            </div>
          )}
        </div>
        <div className="field" style={{ width: 120, marginBottom: 0 }}><label>首期款 / first payment %</label><input type="number" value={advance} onChange={(e) => setAdvance(e.target.value)} placeholder="留空=按模板无预付款" /></div>
        <div className="field" style={{ width: 120, marginBottom: 0 }}><label>质保 / retention %</label><input type="number" value={retention} onChange={(e) => setRetention(e.target.value)} /></div>
        <div className="field" style={{ width: 110, marginBottom: 0 }}><label>DLP / months</label><input type="number" value={dlp} onChange={(e) => setDlp(e.target.value)} /></div>
        <div className="field" style={{ width: 180, marginBottom: 0 }}><label>合同编号 / contract no.</label><input value={contractId} onChange={(e) => setContractId(e.target.value)} placeholder="可留空" /></div>
        <button className="btn-primary" disabled={busy} onClick={generate}>{busy ? '生成中…' : '生成 / Generate'}</button>
      </div>
      {/* 改改 9/20 (fb_3267bf3b): this line used to promise 「可在后续细化为多期进度」 and nothing delivered it
          — 付款计划 now does, right below, as soon as the subcontract exists. */}
      {/* 改改 9/22 · Eric 9/23: new subcontracts start on the company contract template's own clause (4.2.2–4.2.6),
          so the plan and the .docx it generates agree (the old default was one 竣工 100% batch). */}
      <p className="small muted" style={{ margin: '8px 0 0' }}>默认按公司合同模板（4.2.2–4.2.6）：无预付款 → 隐蔽工程验收合格后付至合同总价 50% → 全部完成并验收合格后付至 80% → 竣工结算完成后付至结算总额 95% → 质保金 5%（不抵扣）；建立后可在下方「付款计划（含税）」中用「✎ 编辑付款批次」按本合同实际条款修改。如本合同有首期款：默认各期已占 80%，首期款最多 15%（超过请建立后一并调整各期）。/ Defaults to the contract template's own clause — no advance → 50% of the contract sum once the concealed works pass → 80% on completion and acceptance → 95% of the settlement amount → 5% retention, nothing recovered; amend them to this contract's terms under 付款计划 below (✎ Edit batches). If this contract does pay a first instalment: the default stages already commit 80%, so at most 15% fits — beyond that, move the stages too, after it exists.</p>
      {err && <div className="small" style={{ color: 'var(--bad)', marginTop: 6 }}>{err}</div>}
    </div>
  );
}
